import TelegramConnections from '../components/telegram/TelegramConnections';
import UnmaskedStatesTab from './p2p/UnmaskedStatesTab';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users, Search, ExternalLink, RefreshCw, Copy,
    ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Calendar, Link2, X, Zap,
    AlertTriangle, Eye, Phone, TrendingUp, ArrowUpRight, MessageSquare, Bot, Circle,
    ScanLine, ShieldAlert, CreditCard, Landmark, Send, ShieldCheck, SlidersHorizontal,
    RotateCcw, Database,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import api, { p2pService } from '../services/api';
import {
    CopyIconButton,
    IntelStatIcon,
    ProviderMonogram,
    SeverityBadge,
    type IntelStatTone,
} from '../components/intel';
import {
    brandOutlineBtn,
    brandChipNeutral, brandStatusSuccess, brandStatusDanger,
    brandInputFocus,
} from '../components/brandChrome';
import { toErrorMessage } from '../utils/safeStr';
import { reportClientError } from '../utils/reportClientError';
import { p2pRecordRowKey } from './p2pTableContracts';
import { useSerialQueries } from '../hooks/useSerialQueries';
import {
    applyTraderSignalFiltersAndSort,
    TRADER_SIGNAL_CHIP_META,
    type TraderSignalChip,
    type TraderSignalSort,
} from './p2p/traderSignalFilters';

// ─── Types ────────────────────────────────────────────────────────────
interface MerchantSignals {
    auto_reply?: string | null;
    pay_time_limit_s?: number | null;
    last_seen?: string | null;
    is_online?: boolean | null;
    is_blocked?: boolean | null;
    recommend?: boolean | null;
    auth_tag?: string[] | null;
    counterparty_filters?: Record<string, any> | null;
    targets_new_accounts?: boolean;
    launch_country?: string | null;
}

// Cross-platform normalized trader signals derived from provenance JSONB.
// Complements MerchantSignals (which focuses on live-state + counterparty
// filters). This one exposes KYC tier + activity tempo + risk hints in one
// shape the frontend can use to build filter chips without knowing the
// platform. Backend: app/services/p2p_signals.py.
type RiskHint =
    | 'sub_30s_release'          // avg release < 30s → suspected automation
    | 'very_short_pay_window'    // buyer has <15 min to pay → high-pressure
    | 'restrictive_filters'      // merchant vets counterparties (OTC-style)
    | 'high_cancel_ratio'        // > 15% of recent orders cancelled
    | 'high_recent_tempo'        // > 500 orders in last 30 days
    | 'low_completion';          // recent completion rate < 85%

interface TraderSignals {
    platform?: string;
    kyc?: {
        tier?: string | number | null;
        trust_level?: string | number | null;
        verified_capital?: boolean | null;
    };
    activity?: {
        last_seen_iso?: string | null;
        is_online_now?: boolean | null;
        avg_pay_time_s?: number | null;
        avg_release_time_s?: number | null;
        recent_orders_30d?: number | null;
        recent_completion_rate?: number | null;
        cancel_count?: number | null;
        positive_rate?: number | null;
        thumbs_up?: number | null;
        lifetime_trade_count?: number | null;
    };
    commercial?: {
        pay_window_s?: number | null;
        premium_pct?: number | null;
        has_counterparty_filters?: boolean;
        counterparty_filters?: Record<string, any> | null;
        launch_country?: string | null;
        recommend?: string | null;
    };
    risk_hints?: RiskHint[];
}

// Human-readable label + short tooltip for each risk hint. Kept close to
// the type so future hints get labelled in one place.
const RISK_HINT_META: Record<RiskHint, { label: string; hint: string; tone: 'warn' | 'flag' }> = {
    sub_30s_release:        { label: 'Bot release',          hint: 'Average crypto release time under 30s — suggests automated fill', tone: 'warn' },
    very_short_pay_window:  { label: 'Short pay window',     hint: 'Buyer has under 15 min to pay — pressure tactic',                tone: 'warn' },
    restrictive_filters:    { label: 'Vetted counterparties',hint: 'Merchant restricts trades by KYC / age / order count (OTC-style)', tone: 'flag' },
    high_cancel_ratio:      { label: 'High cancels',         hint: 'Over 15% of recent orders were cancelled',                       tone: 'flag' },
    high_recent_tempo:      { label: 'High tempo',           hint: 'Over 500 orders in the last 30 days',                            tone: 'flag' },
    low_completion:         { label: 'Low completion',       hint: 'Recent completion rate below 85%',                               tone: 'warn' },
};

interface P2PRecord {
    name: string;
    id: string | null;
    ad_id?: string | null;
    ad_url?: string | null;
    source: string;
    platform: string;
    volume: number | null;
    orders: number | null;
    completion_rate: number | null;
    price_rate: number | null;
    upi_id: string | null;
    wallet_address: string | null;
    phone: string | null;
    upi_ids_all?: string[];
    crypto_addresses_all?: string[];
    phone_numbers_all?: string[];
    telegram_handles_all?: string[];
    payment_links_all?: string[];
    bank_name?: string | null;
    bridge_active?: boolean;
    bridge_info?: {
        identifier_type?: string;
        node_type?: string;
        vasp_slug?: string;
        display_name?: string;
    } | null;
    merchant_signals?: MerchantSignals | null;
    trader_signals?: TraderSignals | null;
    has_upi?: boolean;
    has_wallet?: boolean;
    has_phone?: boolean;
    data_completeness?: number;
    telegram_username?: string | null;
    risk_score: number;
    risk_flags?: string[];
    merchant_info?: {
        reg_date: string;
        user_type: string;
        total_lifetime_orders: number;
        positive_rate?: number | null;
    } | null;
    remarks?: string | null;
    profile_link?: string | null;
    asset?: string | null;
    trade_type?: string | null;
    payment_methods?: any;
    scraped_at: string | null;
    // Jul 25 2026 — first-observed timestamp for this ad. p2p_advertisements.created_at
    // is preserved across ON CONFLICT UPDATEs by every scraper (Postgres contract
    // documented in postgres.py), so it's the age of the ad-listing, not the merchant.
    // days_active = (scraped_at - first_seen_at) rounded to 1 decimal — helps
    // investigators distinguish long-lived OTC desks from throwaway single-day mules.
    first_seen_at?: string | null;
    days_active?: number | null;
    signal_count?: number;
    // Cross-platform fields
    platform_count?: number;
    platforms?: string[];
    combined_volume?: number;
    combined_orders?: number;
    platform_details?: any[];
}

interface MarketRatePlatform {
    platform: string;
    avg_rate: number;
    min_rate: number;
    max_rate: number;
    ad_count: number;
    last_scraped: string | null;
    type: 'spot' | 'p2p';
    premium_pct: number;
}

interface AvailableAsset {
    asset: string;
    count: number;
}

interface SpreadAlert {
    platform: string;
    trader: string;
    rate: number;
    premium_pct: number;
    trade_type: string;
    content: string;
    scraped_at: string | null;
    // Apr 30 2026: backend now distinguishes premium-on-buy (urgent capital movement)
    // from discount-on-sell (fire-sale). 'kind' is set on every alert; older clients
    // ignore it.
    kind?: 'buy_premium' | 'sell_discount';
    reference_rate?: number;
}

interface RepeatOffenderSample {
    id: string;
    platform: string;
    trade_type: string;
    rate: number | null;
    premium_pct: number;
    reference_rate: number | null;
    scraped_at: string | null;
}

interface RepeatOffender {
    advertiser_name: string;
    advertiser_id: string | null;
    platforms: string[];
    anomaly_days: number;
    total_anomalies: number;
    worst_premium_pct: number | null;
    max_abs_pct: number | null;
    first_seen: string | null;
    last_seen: string | null;
    profile_link: string | null;
    sample_alerts: RepeatOffenderSample[];
}

interface PlatformStat {
    platform: string;
    count: number;
    unique_traders: number;
    last_scraped: string | null;
}

// Coverage breakdown — "what % of merchants do we already have extra
// info on?". Surfaced as chips in the header so investigators (and
// partner teams previewing the platform) can see at a glance what
// the data lake actually carries.
interface CoverageStats {
    merchants: number;
    with_phone: number;
    with_phone_pct: number;
    with_upi: number;
    with_upi_pct: number;
    with_wallet: number;
    with_wallet_pct: number;
    with_telegram: number;
    with_telegram_pct: number;
    with_paylink: number;
    with_paylink_pct: number;
    with_bank: number;
    with_bank_pct: number;
    with_auto_reply: number;
    with_auto_reply_pct: number;
    targets_new_accounts: number;
    with_any_extra: number;
    with_any_extra_pct: number;
    phones_total_distinct: number;
    phones_with_cached_identity: number;
    unique_real_names_known: number;
}

const PLATFORM_CHIP = { bg: 'bg-muted', text: 'text-foreground', border: 'border-border' };

const PLATFORM_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    binance: PLATFORM_CHIP,
    bybit: PLATFORM_CHIP,
    okx: PLATFORM_CHIP,
    htx: PLATFORM_CHIP,
    kucoin: PLATFORM_CHIP,
    bitget: PLATFORM_CHIP,
    gate: PLATFORM_CHIP,
    bingx: PLATFORM_CHIP,
    hodlhodl: PLATFORM_CHIP,
    wazirx: PLATFORM_CHIP,
    coindcx: PLATFORM_CHIP,
    telegram: PLATFORM_CHIP,
    'cross-platform': PLATFORM_CHIP,
};

const getPlatformStyle = (p: string) => PLATFORM_COLORS[p] || PLATFORM_COLORS.binance;

const TABS = [
    { value: 'all', label: 'All platforms' },
    { value: 'binance', label: 'Binance' },
    { value: 'bybit', label: 'Bybit' },
    { value: 'okx', label: 'OKX' },
    { value: 'htx', label: 'HTX' },
    { value: 'kucoin', label: 'KuCoin' },
    { value: 'bitget', label: 'Bitget' },
    { value: 'gate', label: 'Gate.io' },
    { value: 'bingx', label: 'BingX' },
    { value: 'hodlhodl', label: 'HodlHodl (No-KYC)' },
    { value: 'wazirx', label: 'WazirX' },
    { value: 'coindcx', label: 'CoinDCX' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'privacy', label: 'Privacy chains' },
    { value: 'cross-platform', label: 'Cross-platform' },
    { value: 'cross-source', label: 'P2P ↔ Telegram' },
    { value: 'rings', label: 'Rings' },
    { value: 'offenders', label: 'Repeat offenders' },
    { value: 'unmasked-states', label: 'Phones by State' },
];

const P2P_WORKFLOWS = [
    { value: 'all', label: 'Merchants' },
    { value: 'cross-platform', label: 'Across exchanges' },
    { value: 'cross-source', label: 'Telegram connections' },
    { value: 'rings', label: 'Shared identifiers' },
    { value: 'offenders', label: 'Repeat activity' },
    { value: 'unmasked-states', label: 'Phones by State', description: 'Phones held across Telegram, P2P and harvest sources, bucketed by DoT telecom circle and address records' },
];
const P2P_SOURCE_FILTERS = TABS.filter(tab => !P2P_WORKFLOWS.some(flow => flow.value === tab.value && flow.value !== 'all'));


// Priority order for the token pills (show these first)
const TOKEN_PRIORITY = ['USDT', 'BTC', 'ETH', 'TRX', 'BNB', 'FDUSD'];

// ─── Sub-component: Trader-signal chips ──────────────────────────────
// Renders a compact row of platform-normalized badges under the trader
// name: KYC tier (when set), a "vetted" pill for restrictive filters,
// a "verified capital" pill for HTX-style capital verification, plus
// each risk hint returned by the backend (sub_30s_release, high_tempo,
// etc.). Silent when the trader has no signals — the DB row simply
// hasn't been enriched yet (older rows, or telegram-source rows that
// don't have provenance JSONB).
const TraderSignalsBadges = ({ signals }: { signals?: TraderSignals | null }) => {
    if (!signals) return null;
    const hints = (signals.risk_hints || []) as RiskHint[];
    const kycTier = signals.kyc?.tier;
    const verifiedCapital = signals.kyc?.verified_capital === true;
    const restrictiveFilters = signals.commercial?.has_counterparty_filters;
    const recentOrders = signals.activity?.recent_orders_30d;
    const chips: React.ReactNode[] = [];

    if (kycTier != null && String(kycTier).trim()) {
        chips.push(
            <span key="kyc" className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-foreground border border-border"
                title="Merchant KYC tier reported by platform">
                KYC: {String(kycTier)}
            </span>
        );
    }
    if (verifiedCapital) {
        chips.push(
            <span key="cap" className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20"
                title="Platform has verified this merchant's capital reserves">
                Verified capital
            </span>
        );
    }
    if (restrictiveFilters && !hints.includes('restrictive_filters')) {
        // Only show this as its own chip when the backend didn't already
        // fold "restrictive_filters" into hints — avoids double render.
        chips.push(
            <span key="filters" className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-foreground border border-border"
                title="Merchant restricts counterparties by KYC / age / order count">
                Vetted
            </span>
        );
    }
    if (recentOrders != null && recentOrders >= 100) {
        chips.push(
            <span key="tempo" className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-foreground border border-border tabular-nums"
                title="Orders in the last 30 days (platform-reported)">
                {recentOrders.toLocaleString()}/30d
            </span>
        );
    }
    for (const h of hints) {
        const meta = RISK_HINT_META[h];
        if (!meta) continue;
        const cls = meta.tone === 'warn'
            ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20'
            : 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
        chips.push(
            <span key={`h-${h}`} className={`px-1.5 py-0.5 rounded text-[10px] border ${cls}`} title={meta.hint}>
                {meta.label}
            </span>
        );
    }
    if (!chips.length) return null;
    return <div className="flex flex-wrap gap-1 mt-1">{chips}</div>;
};

/** Compact 90d ads-posted sparkline from GET /p2p/merchant-tempo/{id}. */
const MerchantTempoSparkline = ({ advertiserId }: { advertiserId: string | null | undefined }) => {
    const [state, setState] = React.useState<'idle' | 'loading' | 'empty' | 'error' | 'ready'>('idle');
    const [sparkline, setSparkline] = React.useState<number[]>([]);
    const [summary, setSummary] = React.useState<{ total_ads_posted?: number; peak_ads_posted?: number } | null>(null);

    React.useEffect(() => {
        if (!advertiserId) return;
        let cancelled = false;
        setState('loading');
        api.get(`/p2p/merchant-tempo/${encodeURIComponent(advertiserId)}`, { params: { days: 90 } })
            .then((res) => {
                if (cancelled) return;
                const data = res.data;
                const bars: number[] = Array.isArray(data?.sparkline) ? data.sparkline : [];
                setSparkline(bars);
                setSummary(data?.summary || null);
                setState(bars.length === 0 ? 'empty' : 'ready');
            })
            .catch(() => {
                if (!cancelled) setState('error');
            });
        return () => { cancelled = true; };
    }, [advertiserId]);

    if (!advertiserId) return null;
    if (state === 'loading') {
        return <div className="text-[10px] text-muted-foreground mt-1">Tempo…</div>;
    }
    if (state === 'error') {
        return <div className="text-[10px] text-muted-foreground/70 mt-1" title="Tempo rollup unavailable">Tempo n/a</div>;
    }
    if (state === 'empty') {
        return <div className="text-[10px] text-muted-foreground/70 mt-1" title="No daily activity rollup yet — run rollup_p2p_merchant_activity.py">No tempo yet</div>;
    }
    return (
        <div className="mt-1" title={`90d ads posted: ${summary?.total_ads_posted ?? 0} (peak ${summary?.peak_ads_posted ?? 0}/day)`}>
            <div className="flex items-end gap-px h-5">
                {sparkline.slice(-30).map((v, i) => (
                    <div
                        key={i}
                        className="w-1 bg-primary/60 rounded-sm"
                        style={{ height: `${Math.max(10, Math.round(v * 100))}%` }}
                    />
                ))}
            </div>
            <div className="text-[9px] text-muted-foreground mt-0.5">90d tempo</div>
        </div>
    );
};

// ─── Sub-component: Rings tab (Phase 4+5) ────────────────────────────
// Two-mode surface for the trader-ring detection endpoints:
//   * Discover: browse identifiers appearing on N+ distinct merchants,
//     ranked by cluster size (start-here view when you want to find
//     rings you didn't know about).
//   * Lookup: given a phone / bank / UPI / wallet from a case artefact,
//     list every merchant that advertised with that identifier.
//
// Backend: GET /p2p/rings/discover, GET /p2p/rings/lookup (see
// app/api/v1/p2p.py). No shared store — this tab owns its own state.
interface RingSummary {
    identifier: string;
    merchant_count: number;
    platform_count: number;
    platforms: string[];
    merchant_names_sample: string[];
    merchant_names_total: number;
    first_seen: string | null;
    last_seen: string | null;
}
interface RingMerchant {
    advertiser_id: string | null;
    advertiser_name: string | null;
    platform: string | null;
    profile_link: string | null;
    ad_count: number;
    assets: string[];
    bank_names: string[];
    upi_ids: string[];
    crypto_addresses: string[];
    phone_numbers: string[];
    first_seen_at: string | null;
    last_seen_at: string | null;
}

const RingsTab = () => {
    const [mode, setMode] = React.useState<'discover' | 'lookup'>('discover');
    // Discover state
    const [discoverType, setDiscoverType] = React.useState<'phone' | 'bank'>('phone');
    const [minMerchants, setMinMerchants] = React.useState(2);
    const [sinceDays, setSinceDays] = React.useState(90);
    const [rings, setRings] = React.useState<RingSummary[]>([]);
    const [discoverLoading, setDiscoverLoading] = React.useState(false);
    // Lookup state
    const [lookupValue, setLookupValue] = React.useState('');
    const [lookupType, setLookupType] = React.useState<'phone' | 'bank' | 'upi' | 'wallet'>('phone');
    const [lookupMerchants, setLookupMerchants] = React.useState<RingMerchant[] | null>(null);
    const [lookupSummary, setLookupSummary] = React.useState<{ ad_count: number; merchant_count: number; platform_count: number } | null>(null);
    const [lookupLoading, setLookupLoading] = React.useState(false);

    const runDiscover = React.useCallback(async () => {
        setDiscoverLoading(true);
        try {
            const { data } = await api.get('/p2p/rings/discover', {
                params: { id_type: discoverType, min_merchants: minMerchants, since_days: sinceDays, max_results: 100 },
            });
            setRings((data?.rings || []) as RingSummary[]);
        } catch (err) {
            console.error('Rings discover failed:', err);
            setRings([]);
        } finally {
            setDiscoverLoading(false);
        }
    }, [discoverType, minMerchants, sinceDays]);

    const runLookup = React.useCallback(async (id: string, type: typeof lookupType) => {
        const trimmed = id.trim();
        if (!trimmed) return;
        setLookupLoading(true);
        try {
            const { data } = await api.get('/p2p/rings/lookup', {
                params: { identifier: trimmed, id_type: type, since_days: 180 },
            });
            setLookupMerchants((data?.merchants || []) as RingMerchant[]);
            setLookupSummary({
                ad_count: data?.ad_count ?? 0,
                merchant_count: data?.merchant_count ?? 0,
                platform_count: data?.platform_count ?? 0,
            });
        } catch (err) {
            console.error('Rings lookup failed:', err);
            setLookupMerchants([]);
            setLookupSummary(null);
        } finally {
            setLookupLoading(false);
        }
    }, []);

    // Initial fetch when landing on the Discover mode / changing filters.
    React.useEffect(() => { if (mode === 'discover') runDiscover(); }, [mode, runDiscover]);

    // Clicking a ring row drills into it via the Lookup endpoint — same
    // treatment as if the investigator had pasted the identifier by hand,
    // so behaviour matches the "from a case artefact" path exactly.
    const drillIntoRing = (r: RingSummary) => {
        setMode('lookup');
        setLookupType(discoverType);
        setLookupValue(r.identifier);
        runLookup(r.identifier, discoverType);
    };

    return (
        <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
            <div className="flex flex-col gap-3 border-b border-border/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-sm font-semibold text-foreground">Trader rings</h2>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        {mode === 'discover'
                            ? 'Identifiers shared across multiple merchants — likely rings'
                            : 'Look up every merchant that used one identifier'}
                    </p>
                </div>
                <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                    <button
                        className={`px-3 py-1 ${mode === 'discover' ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setMode('discover')}>Discover</button>
                    <button
                        className={`px-3 py-1 ${mode === 'lookup' ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setMode('lookup')}>Lookup</button>
                </div>
            </div>

            {mode === 'discover' ? (
                <>
                    <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b border-border bg-secondary/10">
                        <label className="text-xs text-muted-foreground">Cluster on
                            <select value={discoverType} onChange={e => setDiscoverType(e.target.value as 'phone' | 'bank')}
                                className="ml-2 bg-secondary border border-border rounded px-2 py-1 text-xs">
                                <option value="phone">Phone number</option>
                                <option value="bank">Bank name (HTX)</option>
                            </select>
                        </label>
                        <label className="text-xs text-muted-foreground">Min merchants
                            <input type="number" min={2} max={100} value={minMerchants}
                                onChange={e => setMinMerchants(Math.max(2, Math.min(100, Number(e.target.value) || 2)))}
                                className="ml-2 bg-secondary border border-border rounded px-2 py-1 text-xs w-16" />
                        </label>
                        <label className="text-xs text-muted-foreground">Since (days)
                            <input type="number" min={1} max={365} value={sinceDays}
                                onChange={e => setSinceDays(Math.max(1, Math.min(365, Number(e.target.value) || 90)))}
                                className="ml-2 bg-secondary border border-border rounded px-2 py-1 text-xs w-16" />
                        </label>
                        <button onClick={runDiscover}
                            className="ml-auto px-3 py-1 rounded text-xs bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30">
                            Refresh
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        {discoverLoading ? (
                            <div className="text-center py-8 text-muted-foreground text-sm">Discovering rings…</div>
                        ) : rings.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground text-sm">
                                No clusters with ≥{minMerchants} merchants in the last {sinceDays} days.
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-secondary/40 text-muted-foreground text-[11px] uppercase">
                                    <tr>
                                        <th className="px-4 py-2 text-left">{discoverType === 'phone' ? 'Phone' : 'Bank'}</th>
                                        <th className="px-4 py-2 text-center">Merchants</th>
                                        <th className="px-4 py-2 text-center">Platforms</th>
                                        <th className="px-4 py-2 text-left">Sample merchants</th>
                                        <th className="px-4 py-2 text-left">Last seen</th>
                                        <th className="px-4 py-2 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {rings.map(r => {
                                        const sample = r.merchant_names_sample.slice(0, 4).join(', ');
                                        const overflow = r.merchant_names_total - Math.min(4, r.merchant_names_sample.length);
                                        return (
                                            <tr key={r.identifier} className="hover:bg-secondary/30 cursor-pointer"
                                                onClick={() => drillIntoRing(r)}>
                                                <td className="px-4 py-2 font-mono text-xs text-foreground">{r.identifier}</td>
                                                <td className="px-4 py-2 text-center font-semibold text-amber-400 tabular-nums">{r.merchant_count}</td>
                                                <td className="px-4 py-2 text-center">
                                                    <div className="flex justify-center flex-wrap gap-1">
                                                        {r.platforms.map(p => {
                                                            const s = getPlatformStyle(p);
                                                            return <span key={p} className={`px-1.5 py-0.5 rounded text-[10px] ${s.bg} ${s.text}`}>{p}</span>;
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 text-xs text-muted-foreground max-w-[360px] truncate" title={r.merchant_names_sample.join(', ')}>
                                                    {sample}{overflow > 0 && <span className="text-foreground/60"> +{overflow} more</span>}
                                                </td>
                                                <td className="px-4 py-2 text-[11px] text-muted-foreground">
                                                    {r.last_seen ? new Date(r.last_seen).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <button onClick={(e) => { e.stopPropagation(); drillIntoRing(r); }}
                                                        className="px-2 py-1 rounded text-[11px] bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30">
                                                        Open ring →
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b border-border bg-secondary/10">
                        <select value={lookupType} onChange={e => setLookupType(e.target.value as typeof lookupType)}
                            className="bg-secondary border border-border rounded px-2 py-1 text-xs">
                            <option value="phone">Phone</option>
                            <option value="bank">Bank</option>
                            <option value="upi">UPI</option>
                            <option value="wallet">Wallet</option>
                        </select>
                        <input value={lookupValue} onChange={e => setLookupValue(e.target.value)}
                            placeholder="Identifier from case artefact (e.g. 9876543210, Optima Bank, TXaBc…)"
                            onKeyDown={e => { if (e.key === 'Enter') runLookup(lookupValue, lookupType); }}
                            className="flex-1 min-w-[240px] bg-secondary border border-border rounded px-2 py-1 text-xs font-mono" />
                        <button onClick={() => runLookup(lookupValue, lookupType)}
                            className="px-3 py-1 rounded text-xs bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30">
                            Lookup
                        </button>
                    </div>
                    {lookupSummary && (
                        <div className="px-4 py-2 text-[11px] text-muted-foreground border-b border-border bg-secondary/10">
                            {lookupSummary.merchant_count} merchant(s) across {lookupSummary.platform_count} platform(s) · {lookupSummary.ad_count} ad(s) total
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        {lookupLoading ? (
                            <div className="text-center py-8 text-muted-foreground text-sm">Looking up…</div>
                        ) : lookupMerchants === null ? (
                            <div className="text-center py-8 text-muted-foreground text-sm">
                                Paste an identifier from a case artefact to see every merchant who advertised with it.
                            </div>
                        ) : lookupMerchants.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground text-sm">
                                No merchants matched that identifier in the last 180 days.
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-secondary/40 text-muted-foreground text-[11px] uppercase">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Merchant</th>
                                        <th className="px-4 py-2 text-left">Platform</th>
                                        <th className="px-4 py-2 text-left">Also uses</th>
                                        <th className="px-4 py-2 text-center">Ads</th>
                                        <th className="px-4 py-2 text-left">First seen</th>
                                        <th className="px-4 py-2 text-left">Last seen</th>
                                        <th className="px-4 py-2 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {lookupMerchants.map((m, i) => {
                                        const s = getPlatformStyle(m.platform || 'binance');
                                        const otherIds = [
                                            ...m.upi_ids.map(u => `UPI ${u}`),
                                            ...m.bank_names.map(b => `Bank ${b}`),
                                            ...m.crypto_addresses.slice(0, 2).map(a => `Wallet ${a.slice(0, 8)}…`),
                                            ...m.phone_numbers.map(p => `☎ ${p}`),
                                        ].join(' · ');
                                        return (
                                            <tr key={`${m.platform}-${m.advertiser_id || m.advertiser_name || i}`} className="hover:bg-secondary/30">
                                                <td className="px-4 py-2">
                                                    <div className="font-medium text-foreground text-sm">{m.advertiser_name || 'Unknown'}</div>
                                                    {m.advertiser_id && <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[220px]" title={m.advertiser_id}>{m.advertiser_id}</div>}
                                                </td>
                                                <td className="px-4 py-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${s.bg} ${s.text}`}>{m.platform || 'unknown'}</span>
                                                </td>
                                                <td className="px-4 py-2 text-xs text-muted-foreground max-w-[380px] truncate" title={otherIds}>{otherIds || '—'}</td>
                                                <td className="px-4 py-2 text-center tabular-nums text-foreground/80">{m.ad_count}</td>
                                                <td className="px-4 py-2 text-[11px] text-muted-foreground">
                                                    {m.first_seen_at ? new Date(m.first_seen_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                                                </td>
                                                <td className="px-4 py-2 text-[11px] text-muted-foreground">
                                                    {m.last_seen_at ? new Date(m.last_seen_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    {m.profile_link && (
                                                        <a href={m.profile_link} target="_blank" rel="noreferrer"
                                                            className="px-2 py-1 rounded text-[11px] bg-secondary hover:bg-secondary/70 border border-border">
                                                            Profile ↗
                                                        </a>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </section>
    );
};

// ─── Sub-component: Identifier Cell ──────────────────────────────────
const IdentifierCell = ({ record, expandedUpiId, toggleUpiExpansion, copyToClipboard }: {
    record: P2PRecord;
    expandedUpiId: string | null;
    toggleUpiExpansion: (upi: string) => void;
    copyToClipboard: (text: string, e: React.MouseEvent) => void;
}) => {
    // Renders the FULL identifier set we have on a merchant: every phone,
    // UPI, wallet, plus bank name and any Telegram handles harvested from
    // the remark, nickname, and (for Binance) the autoReplyMsg. Pre-Jun-7
    // this cell only showed the first element of each array, so the rest
    // of the harvest stayed buried in the DB.
    const phones = (record.phone_numbers_all && record.phone_numbers_all.length > 0)
        ? record.phone_numbers_all
        : (record.phone ? [record.phone] : []);
    const upis = (record.upi_ids_all && record.upi_ids_all.length > 0)
        ? record.upi_ids_all
        : (record.upi_id ? [record.upi_id] : []);
    const wallets = (record.crypto_addresses_all && record.crypto_addresses_all.length > 0)
        ? record.crypto_addresses_all
        : (record.wallet_address ? [record.wallet_address] : []);
    const tgHandles = record.telegram_handles_all || [];
    const payLinks = record.payment_links_all || [];
    const hasAny = phones.length || upis.length || wallets.length || tgHandles.length
        || record.telegram_username || record.profile_link || record.bank_name || record.bridge_active;

    return (
        <div className="space-y-1">
            {record.bridge_active && (
                <div className="flex items-center gap-1">
                    <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/35"
                        title={record.bridge_info?.display_name ? `Verified Node: ${record.bridge_info.display_name} (${record.bridge_info.node_type || 'OTC'})` : 'Active VASP / OTC collection account'}
                    >
                        <Zap className="w-2.5 h-2.5 fill-amber-500 text-amber-500 shrink-0" />
                        Banking Bridge Active
                    </span>
                </div>
            )}
            {phones.map((p, i) => (
                <div key={'ph-' + i} className="group/id flex items-center gap-0.5">
                    <span className="inline-flex items-center gap-1 text-xs font-mono text-foreground px-1.5 py-0.5 rounded bg-muted border border-border">
                        <Phone className="w-3 h-3 text-muted-foreground shrink-0" /> {p}
                    </span>
                    <CopyIconButton value={p} onCopy={copyToClipboard} parentGroup="group/id" />
                </div>
            ))}
            {upis.map((u, i) => (
                <div key={'u-' + i} className="group/id flex items-center gap-0.5">
                    <span className="inline-flex items-center gap-1 text-xs font-mono text-foreground px-1.5 py-0.5 rounded bg-muted border border-border cursor-pointer hover:bg-muted/80"
                        onClick={(e) => { e.stopPropagation(); toggleUpiExpansion(u); }}>
                        <CreditCard className="w-3 h-3 text-muted-foreground shrink-0" /> {u}
                    </span>
                    <CopyIconButton value={u} onCopy={copyToClipboard} parentGroup="group/id" />
                </div>
            ))}
            {record.bank_name && (
                <div className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/25" title="Bank / payment rail reported on the ad">
                    <Landmark className="w-3 h-3 text-blue-500 shrink-0" /> {record.bank_name}
                </div>
            )}
            {wallets.map((w, i) => (
                <div key={'w-' + i} className="group/id flex items-center gap-0.5">
                    <span className="inline-flex items-center gap-1 text-xs font-mono text-foreground px-1.5 py-0.5 rounded bg-muted border border-border" title={w}>
                        <Link2 className="w-3 h-3 text-muted-foreground shrink-0" /> {w.substring(0, 8)}...{w.slice(-4)}
                    </span>
                    <CopyIconButton value={w} onCopy={copyToClipboard} parentGroup="group/id" />
                </div>
            ))}
            {tgHandles.map((h, i) => (
                <div key={'tg-' + i} className="inline-flex items-center gap-1 text-[10px] text-foreground px-1.5 py-0.5 rounded bg-muted border border-border">
                    <Send className="w-2.5 h-2.5 text-muted-foreground shrink-0" />@{h}
                </div>
            ))}
            {record.telegram_username && (
                <div className="inline-flex items-center gap-1 text-[10px] text-foreground px-1.5 py-0.5 rounded bg-muted border border-border">
                    <Send className="w-2.5 h-2.5 text-muted-foreground shrink-0" />@{record.telegram_username}
                </div>
            )}
            {payLinks.length > 0 && (
                <div className="inline-flex items-center gap-1 text-[10px] text-foreground px-1.5 py-0.5 rounded bg-muted border border-border truncate max-w-[200px]" title={payLinks.join(', ')}>
                    <ExternalLink className="w-2.5 h-2.5 text-muted-foreground shrink-0" /> {payLinks[0]}{payLinks.length > 1 ? ' +' + (payLinks.length - 1) : ''}
                </div>
            )}
            {record.profile_link && (
                <a href={record.profile_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:text-primary/80 flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <ExternalLink className="w-3 h-3" />Profile
                </a>
            )}
            {record.remarks && (
                <div className="text-[10px] text-muted-foreground truncate max-w-[200px]" title={record.remarks}>{'📝'} {record.remarks}</div>
            )}
            {!hasAny && <span className="text-muted-foreground/80 text-xs">—</span>}
        </div>
    );
};


// ─── Main Component ──────────────────────────────────────────────────
const P2PIntelligence = () => {
    const navigate = useNavigate();
    const [records, setRecords] = useState<P2PRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [recordsRefreshing, setRecordsRefreshing] = useState(false);
    const recordsAbortRef = useRef<AbortController | null>(null);
    const recordsReqRef = useRef(0);
    const recordsLenRef = useRef(0);
    const [search, setSearch] = useState('');
    const [filterSource, setFilterSource] = useState('all');
    const [hasPhone, setHasPhone] = useState(false);
    // Spread filter + sort. minSpreadAbs hides any row with |spread%| < threshold
    // (set to 0 to show all rows). spreadSort toggles sorting by absolute spread.
    const [minSpreadAbs, setMinSpreadAbs] = useState(0);
    const [spreadSort, setSpreadSort] = useState<'off' | 'desc' | 'asc'>('off');
    // P1-P6 — top-level trader-signal filter chips + activity sorts
    const [signalChips, setSignalChips] = useState<Set<TraderSignalChip>>(new Set());
    const [signalSort, setSignalSort] = useState<TraderSignalSort>('off');
    const [signalSortDir, setSignalSortDir] = useState<'asc' | 'desc'>('desc');

    // Row expansion — investigators expand a row to see the full PII
    // dossier (every harvested phone/UPI/wallet/bank/TG handle, the
    // merchant's auto-reply, counterparty filters, last-seen) plus
    // inline OSINT enrichment per phone (TrueCaller India / ICMR /
    // IndiaMART hits from the identity_pivots cache). Phones stay
    // hidden in the main row by default; full PII shown only on expand.
    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
    const [osintByPhone, setOsintByPhone] = useState<Record<string, any>>({});
    const [osintLoadingByPhone, setOsintLoadingByPhone] = useState<Set<string>>(new Set());

    const toggleRowExpand = useCallback((rowKey: string, phonesToFetch: string[]) => {
        setExpandedRowIds(prev => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else {
                next.add(rowKey);
                // Auto-fetch cached OSINT for every phone on first expand.
                // Misses come back as found=false; we don't auto-trigger the
                // (paid) live LeakOSINT lookup — that's a separate button.
                for (const ph of phonesToFetch) {
                    if (ph && osintByPhone[ph] === undefined && !osintLoadingByPhone.has(ph)) {
                        setOsintLoadingByPhone(s => { const n = new Set(s); n.add(ph); return n; });
                        api.get(`/identity/by-phone/${encodeURIComponent(ph)}`)
                            .then(r => setOsintByPhone(p => ({ ...p, [ph]: r.data })))
                            .catch(() => setOsintByPhone(p => ({ ...p, [ph]: { found: false, error: true } })))
                            .finally(() => setOsintLoadingByPhone(s => { const n = new Set(s); n.delete(ph); return n; }));
                    }
                }
            }
            return next;
        });
    }, [osintByPhone, osintLoadingByPhone]);

    const triggerPhoneEnrichment = useCallback(async (phone: string, wallet?: string | null, upi?: string | null) => {
        setOsintLoadingByPhone(s => { const n = new Set(s); n.add(phone); return n; });
        try {
            const { data } = await api.post('/identity/enrich-phone', {
                phone, linked_wallet: wallet || undefined, linked_upi: upi || undefined,
            });
            setOsintByPhone(p => ({ ...p, [phone]: data }));
        } catch (e: any) {
            setOsintByPhone(p => ({ ...p, [phone]: { found: false, error: toErrorMessage(e, 'Enrichment failed') } }));
        } finally {
            setOsintLoadingByPhone(s => { const n = new Set(s); n.delete(phone); return n; });
        }
    }, []);

    // Stats
    const [totalRecords, setTotalRecords] = useState(0);
    const [platformStats, setPlatformStats] = useState<PlatformStat[]>([]);
    const [coverage, setCoverage] = useState<CoverageStats | null>(null);
    const [marketOpen, setMarketOpen] = useState(true);

    // Market Snapshot
    const [marketRates, setMarketRates] = useState<MarketRatePlatform[]>([]);
    const [referenceRate, setReferenceRate] = useState<number | null>(null);

    // Spread Alerts
    const [spreadAlerts, setSpreadAlerts] = useState<SpreadAlert[]>([]);

    // Repeat Offenders (multi-day anomaly pattern)
    const [repeatOffenders, setRepeatOffenders] = useState<RepeatOffender[]>([]);
    const [offenderExpanded, setOffenderExpanded] = useState<string | null>(null);

    // Token / Asset selector
    const [selectedAsset, setSelectedAsset] = useState('USDT');
    const [availableAssets, setAvailableAssets] = useState<AvailableAsset[]>([]);
    const selectedAssetRef = useRef(selectedAsset);
    selectedAssetRef.current = selectedAsset;

    const chrome = useSerialQueries<any>([
        {
            key: 'assets',
            timeout: 15_000,
            fn: async (signal) => {
                const res = await api.get('/p2p/available-assets', { signal, timeout: 15_000 });
                return res.data;
            },
        },
        {
            key: 'stats',
            timeout: 60_000,
            fn: async (signal) => {
                const res = await api.get('/p2p/stats', { signal, timeout: 60_000 });
                return res.data;
            },
        },
        {
            key: 'market',
            timeout: 60_000,
            fn: async (signal) => {
                const asset = selectedAssetRef.current;
                const [ratesRes, alertsRes] = await Promise.all([
                    api.get('/p2p/market-rates', { params: { asset }, signal, timeout: 60_000 })
                        .catch(() => ({ data: { platforms: [], reference_rate: null } })),
                    api.get('/p2p/spread-alerts', { params: { asset }, signal })
                        .catch(() => ({ data: { alerts: [] } })),
                ]);
                return { rates: ratesRes.data, alerts: alertsRes.data };
            },
        },
    ], { concurrency: 2 });

    useEffect(() => {
        const data = chrome.results.assets;
        if (!data?.assets) return;
        const assets: AvailableAsset[] = data.assets || [];
        const sorted = [...assets].sort((a, b) => {
            const ai = TOKEN_PRIORITY.indexOf(a.asset);
            const bi = TOKEN_PRIORITY.indexOf(b.asset);
            if (ai >= 0 && bi >= 0) return ai - bi;
            if (ai >= 0) return -1;
            if (bi >= 0) return 1;
            return b.count - a.count;
        });
        setAvailableAssets(sorted);
    }, [chrome.results.assets]);

    useEffect(() => {
        const data = chrome.results.stats;
        if (!data) return;
        setPlatformStats(data.platforms || []);
        setTotalRecords(data.total_traders || 0);
        setCoverage(data.coverage || null);
    }, [chrome.results.stats]);

    useEffect(() => {
        const data = chrome.results.market;
        if (!data) return;
        setMarketRates(data.rates?.platforms || []);
        setReferenceRate(data.rates?.reference_rate ?? null);
        setSpreadAlerts(data.alerts?.alerts || []);
    }, [chrome.results.market]);

    // Cross-platform match count (for tab badge)
    const [crossPlatformCount, setCrossPlatformCount] = useState(0);

    // Cross-source identity matches (P2P trader ↔ Telegram sender via shared
    // phone / UPI / wallet / @handle). Populated by the new
    // /p2p/cross-source-identities endpoint.

    // Pagination
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [total, setTotal] = useState(0);

    // UPI-Wallet Linking
    const [linkModal, setLinkModal] = useState<{ open: boolean; upiId: string; walletAddress: string; mode: 'upi-to-wallet' | 'wallet-to-upi' }>({ open: false, upiId: '', walletAddress: '', mode: 'upi-to-wallet' });
    const [linkLoading, setLinkLoading] = useState(false);
    const [autoLinkLoading, setAutoLinkLoading] = useState(false);
    const [linkResult, setLinkResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [refreshingMerchant, setRefreshingMerchant] = useState<string | null>(null);

    // UPI Wallet Quick View
    const [expandedUpiId, setExpandedUpiId] = useState<string | null>(null);
    const [linkedWallets, setLinkedWallets] = useState<any[]>([]);
    const [walletsLoading, setWalletsLoading] = useState(false);

    // ─── Telegram dedup ─────────────────────────────────────────────
    const dedupeTelegramSignals = (rows: P2PRecord[]): P2PRecord[] => {
        const merged = new Map<string, P2PRecord>();
        for (const row of rows) {
            if ((row.platform || '') !== 'telegram') {
                merged.set(`${row.platform}::${row.id || row.name}::${row.scraped_at || ''}`, row);
                continue;
            }
            const key = [row.source, (row.name || '').trim().toLowerCase(), (row.upi_id || '').trim().toLowerCase(),
                (row.wallet_address || '').trim().toLowerCase(), (row.phone || '').trim().toLowerCase()].join('||');
            const existing = merged.get(key);
            if (!existing) { merged.set(key, { ...row, signal_count: 1 }); continue; }
            const prevTime = existing.scraped_at ? new Date(existing.scraped_at).getTime() : 0;
            const nextTime = row.scraped_at ? new Date(row.scraped_at).getTime() : 0;
            merged.set(key, { ...(nextTime > prevTime ? row : existing), signal_count: (existing.signal_count || 1) + 1 });
        }
        return Array.from(merged.values());
    };

    // ─── Data Fetching ──────────────────────────────────────────────
    const marketAbortRef = useRef<AbortController | null>(null);
    const statsAbortRef = useRef<AbortController | null>(null);

    const fetchMarketData = useCallback(async (fresh = false) => {
        marketAbortRef.current?.abort();
        const ac = new AbortController();
        marketAbortRef.current = ac;
        try {
            const params: Record<string, string | boolean> = { asset: selectedAsset };
            if (fresh) params.fresh = true;
            const [ratesRes, alertsRes] = await Promise.all([
                api.get('/p2p/market-rates', { params, signal: ac.signal, timeout: 60_000 }).catch(() => ({ data: { platforms: [], reference_rate: null } })),
                api.get('/p2p/spread-alerts', { params: { asset: selectedAsset }, signal: ac.signal }).catch(() => ({ data: { alerts: [] } })),
            ]);
            if (ac.signal.aborted) return;
            setMarketRates(ratesRes.data.platforms || []);
            setReferenceRate(ratesRes.data.reference_rate);
            setSpreadAlerts(alertsRes.data.alerts || []);
        } catch { /* non-critical */ }
    }, [selectedAsset]);

    const fetchRepeatOffenders = useCallback(async () => {
        try {
            const offendersRes = await api.get('/p2p/repeat-offenders', {
                params: { asset: selectedAsset, threshold_pct: 5, min_days: 2, lookback_days: 30, limit: 30 }
            });
            setRepeatOffenders(offendersRes.data.offenders || []);
        } catch {
            setRepeatOffenders([]);
        }
    }, [selectedAsset]);

    const fetchCrossPlatformCount = useCallback(async () => {
        try {
            const crossRes = await api.get('/p2p/cross-platform?limit=1&page=1');
            setCrossPlatformCount(crossRes.data.total || 0);
        } catch {
            setCrossPlatformCount(0);
        }
    }, []);

    const fetchStats = useCallback(async (fresh = false) => {
        statsAbortRef.current?.abort();
        const ac = new AbortController();
        statsAbortRef.current = ac;
        try {
            const { data } = await api.get('/p2p/stats', {
                params: fresh ? { fresh: true } : {},
                signal: ac.signal,
                timeout: 60_000,
            });
            if (ac.signal.aborted) return;
            setPlatformStats(data.platforms || []);
            setTotalRecords(data.total_traders || 0);
            setCoverage(data.coverage || null);
        } catch { /* non-critical */ }
    }, []);

    const fetchRecords = useCallback(async (opts?: { fresh?: boolean }) => {
        if (['rings','cross-source','offenders'].includes(filterSource)) {
            setRecords([]);
            setTotal(0);
            return;
        }
        recordsAbortRef.current?.abort();
        const ac = new AbortController();
        recordsAbortRef.current = ac;
        const reqId = ++recordsReqRef.current;
        if (recordsLenRef.current === 0) setLoading(true);
        else setRecordsRefreshing(true);
        try {
            const params: Record<string, any> = { source: filterSource, has_phone: hasPhone, limit, page };
            if (selectedAsset && filterSource !== 'telegram' && filterSource !== 'cross-platform') {
                params.asset = selectedAsset;
            }
            if (opts?.fresh) params.fresh = true;
            const { data: response } = await api.get('/p2p/unified', { params, signal: ac.signal });
            if (reqId !== recordsReqRef.current) return;
            const next = dedupeTelegramSignals(response.data || []);
            recordsLenRef.current = next.length;
            setRecords(next);
            setTotal(response.total || 0);
        } catch (err: any) {
            if (ac.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
            if (reqId !== recordsReqRef.current) return;
            console.error("Failed to fetch P2P intelligence:", err);
            reportClientError(err, { surface: 'p2p', endpoint: '/p2p/unified' });
        } finally {
            if (reqId === recordsReqRef.current) {
                setLoading(false);
                setRecordsRefreshing(false);
            }
        }
    }, [filterSource, hasPhone, limit, page, selectedAsset]);

    const handleRefreshMerchant = useCallback(async (advertiserId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setRefreshingMerchant(advertiserId);
        try {
            await api.post(`/p2p/refresh-merchant/${advertiserId}`);
            fetchRecords({ fresh: true });
            fetchMarketData(true);
        } catch (err) {
            console.error('Failed to refresh merchant:', err);
        } finally {
            setRefreshingMerchant(null);
        }
    }, [fetchRecords, fetchMarketData]);

    const fetchAvailableAssets = useCallback(async (fresh = false) => {
        try {
            const res = await api.get('/p2p/available-assets', { params: fresh ? { fresh: true } : {} });
            const assets: AvailableAsset[] = res.data?.assets || [];
            const sorted = assets.sort((a, b) => {
                const ai = TOKEN_PRIORITY.indexOf(a.asset);
                const bi = TOKEN_PRIORITY.indexOf(b.asset);
                if (ai >= 0 && bi >= 0) return ai - bi;
                if (ai >= 0) return -1;
                if (bi >= 0) return 1;
                return b.count - a.count;
            });
            setAvailableAssets(sorted);
        } catch { /* non-critical */ }
    }, []);

    useEffect(() => { fetchRecords(); }, [fetchRecords]);
    const skipAssetMarketRef = useRef(true);
    useEffect(() => {
        if (skipAssetMarketRef.current) {
            skipAssetMarketRef.current = false;
            return;
        }
        fetchMarketData();
    }, [selectedAsset, fetchMarketData]);
    useEffect(() => {
        return () => {
            recordsAbortRef.current?.abort();
            marketAbortRef.current?.abort();
            statsAbortRef.current?.abort();
        };
    }, []);
    useEffect(() => {
        if (filterSource === 'offenders') fetchRepeatOffenders();
    }, [filterSource, fetchRepeatOffenders]);
    useEffect(() => {
        if (filterSource === 'cross-platform') fetchCrossPlatformCount();
    }, [filterSource, fetchCrossPlatformCount]);

    // ─── Helpers ────────────────────────────────────────────────────
    const fetchLinkedWallets = async (upiId: string) => {
        setWalletsLoading(true);
        try {
            const { data } = await api.get(`/p2p/upi/${encodeURIComponent(upiId)}/linked-wallets`);
            const walletsRaw = Array.isArray(data) ? data : (data?.wallets || []);
            setLinkedWallets(walletsRaw.map((w: any) =>
                typeof w === 'string' ? { address: w, chain: null, link_type: 'inferred', confidence: null } : w
            ));
        } catch { setLinkedWallets([]); }
        finally { setWalletsLoading(false); }
    };

    const toggleUpiExpansion = (upiId: string) => {
        if (expandedUpiId === upiId) { setExpandedUpiId(null); setLinkedWallets([]); }
        else { setExpandedUpiId(upiId); fetchLinkedWallets(upiId); }
    };

    const formatRate = (val: number) => {
        if (val >= 10000) return '₹' + val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
        return '₹' + val.toFixed(2);
    };

    // Available balance is the scraped `surplusAmount` / `availableAmount` /
    // `lastQuantity` from each exchange — i.e. asset units (USDT / BTC / etc.),
    // NOT INR. Before Jun 7 2026 we ran it through formatCurrency() which
    // prefixed it with ₹, so a Binance merchant's 2,000,000 USDT cap was
    // rendered as "₹20,00,000" (reads like ₹20 lakh, actually ~₹17 crore).
    // Now we display the native asset units and quote the INR equivalent
    // below when we have a price_rate to multiply by.
    const formatAssetBalance = (amount: number | null | undefined, asset: string | null | undefined, priceRate?: number | null) => {
        if (amount == null || isNaN(amount)) return <span className="text-muted-foreground/80 text-xs">&mdash;</span>;
        const sym = (asset || 'USDT').toUpperCase();
        // BTC/ETH/BNB keep small decimals; stablecoins round to whole units.
        const isSmallUnit = ['BTC', 'ETH', 'BNB'].includes(sym);
        const formatted = amount >= 1000 || !isSmallUnit
            ? amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })
            : amount.toLocaleString('en-IN', { maximumFractionDigits: 4 });
        // INR equivalent only when we actually have a per-unit rate.
        let inr: string | null = null;
        if (priceRate && priceRate > 0 && amount > 0) {
            const value = amount * priceRate;
            if (value >= 1e7) inr = `~₹${(value / 1e7).toFixed(2)} Cr`;
            else if (value >= 1e5) inr = `~₹${(value / 1e5).toFixed(2)} L`;
            else inr = `~₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
        }
        return (
            <div className="leading-tight">
                <div className="text-foreground/90 tabular-nums">
                    {formatted} <span className="text-[10px] text-muted-foreground uppercase">{sym}</span>
                </div>
                {inr && <div className="text-[10px] text-muted-foreground tabular-nums">{inr}</div>}
            </div>
        );
    };

    const getRiskLabel = (score: number) => score >= 0.7 ? 'HIGH' : score >= 0.4 ? 'MEDIUM' : 'LOW';

    const handleRowClick = (record: P2PRecord) => {
        if (record.wallet_address) navigate(`/workbench?address=${record.wallet_address}&chain=TRON`);
        else if (record.upi_id) navigate(`/workbench?upi=${record.upi_id}`);
        else if (record.phone) navigate(`/workbench?phone=${record.phone}`);
    };

    const copyToClipboard = (text: string, e: React.MouseEvent) => { e.stopPropagation(); navigator.clipboard.writeText(text); };

    const openLinkModal = (record: P2PRecord, e: React.MouseEvent) => {
        e.stopPropagation();
        if (record.upi_id && !record.wallet_address) setLinkModal({ open: true, upiId: record.upi_id, walletAddress: '', mode: 'upi-to-wallet' });
        else if (record.wallet_address && !record.upi_id) setLinkModal({ open: true, upiId: '', walletAddress: record.wallet_address, mode: 'wallet-to-upi' });
        else if (record.upi_id) setLinkModal({ open: true, upiId: record.upi_id, walletAddress: record.wallet_address || '', mode: 'upi-to-wallet' });
        setLinkResult(null);
    };

    const handleLink = async () => {
        if (!linkModal.upiId || !linkModal.walletAddress) return;
        setLinkLoading(true); setLinkResult(null);
        try {
            await p2pService.linkUpiToWallet(linkModal.upiId, linkModal.walletAddress, 'manual_investigation');
            setLinkResult({ type: 'success', message: `Linked ${linkModal.upiId} \u2192 ${linkModal.walletAddress.substring(0, 10)}...` });
            setTimeout(() => { setLinkModal({ open: false, upiId: '', walletAddress: '', mode: 'upi-to-wallet' }); setLinkResult(null); }, 1500);
        } catch (err: any) {
            setLinkResult({ type: 'error', message: toErrorMessage(err, 'Failed to create link') });
        } finally { setLinkLoading(false); }
    };

    const handleAutoLink = async () => {
        setAutoLinkLoading(true);
        try {
            const result = await p2pService.autoLinkIdentities();
            const processed = result.links_processed ?? 0;
            setLinkResult({ type: 'success', message: `Auto-link: ${processed} links processed` });
            setTimeout(() => setLinkResult(null), 5000);
            fetchRecords();
        } catch (err: any) {
            setLinkResult({ type: 'error', message: toErrorMessage(err, 'Auto-link failed') });
            setTimeout(() => setLinkResult(null), 5000);
        } finally { setAutoLinkLoading(false); }
    };

    // ─── Derived state ──────────────────────────────────────────────
    const totalPages = Math.ceil(total / limit);
    const isTelegramTab = filterSource === 'telegram';
    const isExchangeTab = ['binance', 'bybit', 'okx', 'htx', 'kucoin', 'bitget'].includes(filterSource);
    const isCrossPlatformTab = filterSource === 'cross-platform';
    const isPrivacyTab = filterSource === 'privacy';
    const isCrossSourceTab = filterSource === 'cross-source';
    const isOffendersTab = filterSource === 'offenders';
    const isRingsTab = filterSource === 'rings';
    const isUnmaskedStatesTab = filterSource === 'unmasked-states';
    const showPlatformCol = filterSource === 'all' || isCrossPlatformTab;

    // Build per-platform worst-magnitude alert for market cards. Apr 30 2026:
    // BUY-side alerts have positive premium, SELL-side alerts have negative
    // premium (price below ask). Compare by absolute value so the chip surfaces
    // whichever direction is most extreme.
    const platformAlertMap = React.useMemo(() => {
        const map: Record<string, SpreadAlert> = {};
        for (const a of spreadAlerts) {
            if (!map[a.platform] || Math.abs(a.premium_pct) > Math.abs(map[a.platform].premium_pct)) {
                map[a.platform] = a;
            }
        }
        return map;
    }, [spreadAlerts]);

    const getColCount = () => {
        if (isCrossPlatformTab) return 7;
        if (isTelegramTab) return 5; // Trader, Channel, Identifiers, Risk, Action
        let cols = 7; // Trader, Volume, Orders, Rate, Spread, Identifiers, Action
        if (showPlatformCol) cols++;
        if (isExchangeTab && filterSource === 'binance') cols++; // Merchant
        return cols;
    };
    const colCount = getColCount();

    // Compute spread (% deviation of trader's price_rate from the platform
    // reference). Returns null when either side is missing so sort/filter can
    // skip the row instead of treating it as a 0% spread.
    const computeSpread = React.useCallback((rate: number | null | undefined): number | null => {
        if (!rate || !referenceRate || referenceRate === 0) return null;
        return ((rate - referenceRate) / referenceRate) * 100;
    }, [referenceRate]);

    const filteredRecords = React.useMemo(() => {
        let rows = search
            ? records.filter(r => r.name?.toLowerCase().includes(search.toLowerCase()))
            : records;

        if (minSpreadAbs > 0) {
            rows = rows.filter(r => {
                const s = computeSpread(r.price_rate);
                return s !== null && Math.abs(s) >= minSpreadAbs;
            });
        }

        // Trader-signal chips + sorts (P1-P6). Applied before spread sort so
        // spread sort can still override when the investigator asks for it.
        rows = applyTraderSignalFiltersAndSort(rows, {
            chips: signalChips,
            sort: signalSort,
            sortDir: signalSortDir,
        });

        if (spreadSort !== 'off') {
            const dir = spreadSort === 'desc' ? -1 : 1;
            rows = [...rows].sort((a, b) => {
                const sa = computeSpread(a.price_rate);
                const sb = computeSpread(b.price_rate);
                // Rows with no spread always sink to the bottom regardless of dir.
                if (sa === null && sb === null) return 0;
                if (sa === null) return 1;
                if (sb === null) return -1;
                return (Math.abs(sa) - Math.abs(sb)) * dir;
            });
        }

        return rows;
    }, [records, search, minSpreadAbs, spreadSort, computeSpread, signalChips, signalSort, signalSortDir]);

    const toggleSignalChip = (chip: TraderSignalChip) => {
        setSignalChips((prev) => {
            const next = new Set(prev);
            // KYC verified/unverified are mutually exclusive
            if (chip === 'kyc_verified') next.delete('kyc_unverified');
            if (chip === 'kyc_unverified') next.delete('kyc_verified');
            if (next.has(chip)) next.delete(chip);
            else next.add(chip);
            return next;
        });
    };

    const refreshAll = () => {
        fetchRecords({ fresh: true });
        fetchMarketData(true);
        fetchStats(true);
        fetchAvailableAssets(true);
        if (filterSource === 'offenders') fetchRepeatOffenders();
        if (filterSource === 'cross-platform') fetchCrossPlatformCount();
    };

    // Cross-source identity matches (P2P ⇄ Telegram)

    // ─── Render ─────────────────────────────────────────────────────
    const activeFilterCount = (isCrossSourceTab || isOffendersTab || isRingsTab || isUnmaskedStatesTab) ? 0 : [
        filterSource !== 'all',
        hasPhone,
        minSpreadAbs > 0,
        Boolean(search.trim()),
        signalChips.size > 0,
    ].filter(Boolean).length;

    const clearExplorerFilters = () => {
        setFilterSource('all');
        setHasPhone(false);
        setMinSpreadAbs(0);
        setSearch('');
        setSignalChips(new Set());
        setPage(1);
    };

    return (
        <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 p-4 sm:p-6 lg:p-8">
            <header className="flex flex-col gap-4 border-b border-border/60 pb-5 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-3xl">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                        <ShieldCheck className="h-4 w-4" aria-hidden />
                        Peer-to-peer market
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                        P2P Intelligence
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                        Browse exchange P2P ads and Telegram signals, then jump from a merchant identifier into the Workbench.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Market coverage">
                        <span><strong className="font-semibold text-foreground">{totalRecords.toLocaleString()}</strong> traders</span>
                        <span><strong className="font-semibold text-foreground">{platformStats.length || '—'}</strong> platforms</span>
                        {crossPlatformCount > 0 && (
                            <span><strong className="font-semibold text-foreground">{crossPlatformCount.toLocaleString()}</strong> cross-platform</span>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 self-start lg:self-auto">
                    <button
                        type="button"
                        onClick={handleAutoLink}
                        disabled={autoLinkLoading}
                        className={`${brandOutlineBtn} inline-flex min-h-10 items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50`}
                        title="Auto-link UPI IDs to wallets from Telegram messages"
                    >
                        <Zap className="h-4 w-4" aria-hidden />
                        {autoLinkLoading ? 'Linking…' : 'Auto-link UPI'}
                    </button>
                    <button
                        type="button"
                        onClick={refreshAll}
                        disabled={loading || recordsRefreshing}
                        className={`${brandOutlineBtn} inline-flex min-h-10 items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        <RefreshCw className={`h-4 w-4 ${(loading || recordsRefreshing) ? 'animate-spin' : ''}`} aria-hidden />
                        Refresh market
                    </button>
                </div>
            </header>

            {linkResult && (
                <div className={`${linkResult.type === 'success' ? brandStatusSuccess : brandStatusDanger} flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs`} role="alert">
                    <span>{linkResult.message}</span>
                    <button type="button" onClick={() => setLinkResult(null)} className="font-semibold underline underline-offset-2">Dismiss</button>
                </div>
            )}

            {/* Crypto ↔ Banking Bridge callout banner */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-xs shadow-sm">
                <div className="flex items-start gap-3">
                    <span className="rounded-lg bg-amber-500/20 p-2 text-amber-600 dark:text-amber-400 shrink-0">
                        <Zap className="h-5 w-5 fill-amber-500 text-amber-500" />
                    </span>
                    <div>
                        <div className="font-semibold text-sm text-foreground flex items-center gap-2 flex-wrap">
                            Crypto ↔ Banking Bridge Active

                        </div>
                        <p className="mt-1 text-muted-foreground leading-relaxed">
                            Compare merchant identifiers with retained collection-account records in Fiat Off-Ramp. Shared identifiers are leads; account ownership requires supporting evidence.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/off-ramp')}
                    className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs transition-colors shadow-sm"
                >
                    <Zap className="w-3.5 h-3.5 fill-black" />
                    Open Off-Ramp
                </button>
            </div>

            <section aria-labelledby="p2p-overview-title">
                <h2 id="p2p-overview-title" className="sr-only">Market overview</h2>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                    <P2pStatCard label="Traders" value={totalRecords} icon={Users} />
                    <P2pStatCard label="Platforms" value={platformStats.length} icon={Database} />
                    <P2pStatCard label="With phone" value={coverage?.with_phone} tone="brand" icon={Phone} />
                    <P2pStatCard label="With UPI" value={coverage?.with_upi} icon={CreditCard} />
                    <P2pStatCard label="With wallet" value={coverage?.with_wallet} icon={Link2} />
                    <P2pStatCard
                        label="Extra signals"
                        value={coverage?.with_any_extra}
                        suffix={coverage != null ? `${coverage.with_any_extra_pct.toFixed(0)}%` : undefined}
                        tone="brand"
                        icon={ScanLine}
                    />
                </div>
            </section>

            {/* Token + market snapshot */}
            <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm" aria-labelledby="p2p-market-title">
                <button
                    type="button"
                    onClick={() => setMarketOpen(!marketOpen)}
                    className="flex min-h-14 w-full items-center justify-between gap-3 px-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    aria-expanded={marketOpen}
                    aria-controls="p2p-market-content"
                >
                    <span className="flex min-w-0 items-center gap-3">
                        <span className="rounded-lg bg-primary/10 p-2 text-primary"><TrendingUp className="h-4 w-4" aria-hidden /></span>
                        <span>
                            <span id="p2p-market-title" className="block text-sm font-semibold text-foreground">{selectedAsset}/INR market snapshot</span>
                            <span className="block text-xs text-muted-foreground">
                                {referenceRate ? `Reference spot ₹${referenceRate.toLocaleString('en-IN')}` : 'Platform rates versus spot'}
                            </span>
                        </span>
                    </span>
                    {marketOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden /> : <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />}
                </button>
                {marketOpen && (
                    <div id="p2p-market-content" className="border-t border-border/60 bg-muted/10 p-4">
                        <div className="mb-3 flex flex-wrap items-center gap-1 rounded-lg bg-muted/50 p-1 sm:w-fit" role="group" aria-label="Asset">
                            {(() => {
                                const tokens = availableAssets.length > 0
                                    ? availableAssets.filter(a => TOKEN_PRIORITY.includes(a.asset)).sort((a, b) => TOKEN_PRIORITY.indexOf(a.asset) - TOKEN_PRIORITY.indexOf(b.asset))
                                    : TOKEN_PRIORITY.map(t => ({ asset: t, count: 0 }));
                                return tokens.map(a => (
                                    <button key={a.asset} type="button"
                                        aria-pressed={selectedAsset === a.asset}
                                        onClick={() => { setSelectedAsset(a.asset); setPage(1); }}
                                        className={`min-h-8 rounded-md px-3 text-xs font-medium transition-colors ${
                                            selectedAsset === a.asset
                                                ? 'bg-card text-foreground shadow-sm'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}>
                                        {a.asset}
                                        {a.count > 0 && <span className="ml-1.5 tabular-nums text-[10px] opacity-60">{a.count.toLocaleString()}</span>}
                                    </button>
                                ));
                            })()}
                        </div>
            {marketRates.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                        {marketRates.map((p) => {
                            const premiumColor = p.premium_pct <= 0.5 ? 'text-green-400' : p.premium_pct <= 3 ? 'text-amber-400' : p.premium_pct <= 5 ? 'text-orange-400' : 'text-red-400';
                            const style = getPlatformStyle(p.platform);
                            const isRange = p.min_rate !== p.max_rate && Math.abs(p.max_rate - p.min_rate) > 0.5;
                            // Tile click filters table to this platform. TABS array uses lowercase platform
                            // names (binance/bybit/okx); fall back to 'all' for anything not in the tab set
                            // so we never strand the user on a tab that doesn't exist.
                            const tabValue = p.platform.toLowerCase();
                            const isFiltered = filterSource === tabValue;
                            const isClickable = TABS.some(t => t.value === tabValue);
                            return (
                                <div key={p.platform}
                                    role={isClickable ? 'button' : undefined}
                                    tabIndex={isClickable ? 0 : undefined}
                                    onClick={isClickable ? () => { setFilterSource(tabValue); setPage(1); } : undefined}
                                    onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilterSource(tabValue); setPage(1); } } : undefined}
                                    title={isClickable ? `Filter table to ${p.platform}` : undefined}
                                    className={`rounded-lg border bg-card p-3 text-left transition-all duration-200 ${style.border} ${
                                        isClickable ? 'cursor-pointer hover:border-primary/40 hover:shadow-md' : ''
                                    } ${isFiltered ? 'ring-2 ring-primary/60' : ''}`}>
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <span className={`text-xs font-bold uppercase ${style.text}`}>{p.platform}</span>
                                        <span
                                            title={p.type === 'spot'
                                                ? 'Exchange order-book ticker — not real P2P merchant activity. Used as reference price.'
                                                : 'Real peer-to-peer ads from human merchants'}
                                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider ${
                                                p.type === 'spot'
                                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                                    : 'bg-primary/15 text-primary border border-blue-500/20'
                                            }`}>
                                            {p.type === 'spot' ? 'exchange ticker' : 'p2p ads'}
                                        </span>
                                    </div>
                                    <div className="text-lg font-bold text-foreground tabular-nums">
                                        {formatRate(p.avg_rate)}
                                    </div>
                                    {isRange && (
                                        <div className="text-[10px] text-muted-foreground tabular-nums">
                                            {formatRate(p.min_rate)} – {formatRate(p.max_rate)}
                                        </div>
                                    )}
                                    <div className={`text-xs font-semibold mt-1 flex items-center gap-0.5 ${premiumColor}`}>
                                        {p.premium_pct > 0 ? (
                                            <><ArrowUpRight className="w-3 h-3" />+{p.premium_pct}%</>
                                        ) : (
                                            <span className="text-green-400">baseline</span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground/80 mt-1">
                                        {p.ad_count} ads
                                        {p.last_scraped && (() => {
                                            // Binance scrapes daily; Bybit/OKX/HTX/KuCoin/Bitget + tickers
                                            // scrape weekly, so their card price can be several days old.
                                            // Flag age only when stale (>36h) so fresh cards stay uncluttered.
                                            const ageH = (Date.now() - new Date(p.last_scraped).getTime()) / 3.6e6;
                                            if (!isFinite(ageH) || ageH < 36) return null;
                                            const d = Math.max(1, Math.round(ageH / 24));
                                            return (
                                                <span className="ml-1 text-amber-400/70"
                                                    title={`Last scraped ${new Date(p.last_scraped!).toLocaleString()}`}>
                                                    · {d}d ago
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    {platformAlertMap[p.platform] && (() => {
                                        const a = platformAlertMap[p.platform];
                                        const isBuyPremium = a.kind === 'buy_premium';
                                        const colorClass = isBuyPremium ? 'text-red-400 border-red-500/20' : 'text-amber-400 border-amber-500/20';
                                        const sign = a.premium_pct >= 0 ? '+' : '';
                                        const label = isBuyPremium ? 'BUY premium' : 'SELL discount';
                                        const tooltip = a.kind
                                            ? a.content
                                            : `Worst spread vs reference: ${sign}${a.premium_pct.toFixed(1)}%`;
                                        return (
                                            <div className={`mt-1.5 pt-1.5 border-t ${colorClass.split(' ')[1]}`} title={tooltip}>
                                                <div className={`flex items-center gap-1 text-[10px] ${colorClass.split(' ')[0]}`}>
                                                    <AlertTriangle className="w-3 h-3" />
                                                    <span>{label} {sign}{a.premium_pct.toFixed(1)}%</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                )}
                    </div>
                )}
            </section>

            <section className="rounded-xl border border-border/70 bg-card p-4 shadow-sm" aria-labelledby="p2p-filters-title">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden />
                        <h2 id="p2p-filters-title" className="text-sm font-semibold text-foreground">Explore P2P intelligence</h2>
                        {activeFilterCount > 0 && (
                            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                {activeFilterCount} active
                            </span>
                        )}
                    </div>
                    {activeFilterCount > 0 && (
                        <button type="button" onClick={clearExplorerFilters} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
                            <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Clear filters
                        </button>
                    )}
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-1 rounded-lg bg-muted/50 p-1" aria-label="P2P workflows">
                    {P2P_WORKFLOWS.map(tab => (
                        <button key={tab.value} type="button" title={'description' in tab ? tab.description : undefined}
                            aria-pressed={filterSource === tab.value || (tab.value === 'all' && P2P_SOURCE_FILTERS.some(source => source.value === filterSource))}
                            onClick={() => { setFilterSource(tab.value); setPage(1); }}
                            className={`min-h-8 rounded-md px-3 text-xs font-medium transition-colors ${
                                filterSource === tab.value || (tab.value === 'all' && P2P_SOURCE_FILTERS.some(source => source.value === filterSource))
                                    ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}>
                            {tab.label}
                        </button>
                    ))}
                </div>
                {P2P_SOURCE_FILTERS.some(source => source.value === filterSource) && (
                    <label className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
                        Data source
                        <select aria-label="P2P data source" value={filterSource}
                            onChange={event => { setFilterSource(event.target.value); setPage(1); }}
                            className="min-h-9 rounded-md border border-input bg-background px-2 text-foreground">
                            {P2P_SOURCE_FILTERS.map(source => <option key={source.value} value={source.value}>{source.label}</option>)}
                        </select>
                        <span>Exchange tickers are market context; merchant ads supply identity evidence.</span>
                    </label>
                )}

                {!isCrossSourceTab && !isOffendersTab && !isRingsTab && !isUnmaskedStatesTab && (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                    <div className="relative md:col-span-5">
                        <label htmlFor="p2p-search" className="sr-only">Search merchant name</label>
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <input id="p2p-search" type="search" placeholder="Search merchant name"
                            className={`min-h-10 w-full rounded-lg border border-input bg-background pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground ${brandInputFocus}`}
                            value={search} onChange={(e) => setSearch(e.target.value)} />
                        {search && (
                            <button type="button" onClick={() => setSearch('')} className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Clear search">
                                <X className="h-4 w-4" aria-hidden />
                            </button>
                        )}
                    </div>
                    <label className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-input bg-background px-3 text-xs md:col-span-3">
                        <input type="checkbox" id="hasPhone" checked={hasPhone}
                            onChange={(e) => { setHasPhone(e.target.checked); setPage(1); }}
                            className="h-4 w-4 rounded border-input accent-primary" />
                        <span className={hasPhone ? 'font-medium text-foreground' : 'text-muted-foreground'}>Has phone</span>
                    </label>
                    {!isTelegramTab && !isPrivacyTab && !isCrossPlatformTab && (
                        <div className="flex min-h-10 items-center gap-2 rounded-lg border border-input bg-background px-3 md:col-span-4"
                            title="Hide ads whose price deviates from the platform reference rate by less than this">
                            <label htmlFor="minSpread" className="text-xs text-muted-foreground whitespace-nowrap">|spread| ≥</label>
                            <input id="minSpread" type="number" min={0} max={100} step={0.5}
                                value={minSpreadAbs}
                                onChange={(e) => { setMinSpreadAbs(Math.max(0, Number(e.target.value) || 0)); }}
                                className={`w-16 bg-transparent text-sm tabular-nums text-foreground ${brandInputFocus}`} />
                            <span className="text-xs text-muted-foreground">%</span>
                        </div>
                    )}
                </div>
                )}


            {/* P1-P6 — trader-signal filter chips + activity sorts */}
            {!isTelegramTab && !isPrivacyTab && !isCrossPlatformTab && !isOffendersTab && !isRingsTab && !isCrossSourceTab && !isUnmaskedStatesTab && (
                <div className="mt-3.5 pt-3 border-t border-border/50 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mr-1">Signals</span>
                    {(Object.keys(TRADER_SIGNAL_CHIP_META) as TraderSignalChip[]).map((chip) => {
                        const meta = TRADER_SIGNAL_CHIP_META[chip];
                        const active = signalChips.has(chip);
                        return (
                            <button
                                key={chip}
                                type="button"
                                aria-pressed={active}
                                title={meta.hint}
                                onClick={() => toggleSignalChip(chip)}
                                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                                    active
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-secondary/30 text-muted-foreground border-border hover:text-foreground hover:border-primary/40'
                                }`}
                            >
                                {meta.label}
                            </button>
                        );
                    })}
                    <div className="flex items-center gap-1.5 ml-auto">
                        <label htmlFor="signalSort" className="text-xs text-muted-foreground">Sort</label>
                        <select
                            id="signalSort"
                            value={signalSort}
                            onChange={(e) => setSignalSort(e.target.value as TraderSignalSort)}
                            className="bg-secondary/30 border border-border rounded-lg px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                            <option value="off">Default</option>
                            <option value="recent_orders_30d">Recent orders (30d)</option>
                            <option value="cancel_count">Cancel count</option>
                            <option value="pay_window_s">Pay window</option>
                        </select>
                        {signalSort !== 'off' && (
                            <button
                                type="button"
                                onClick={() => setSignalSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
                                className="text-xs px-2 py-1 rounded border border-border bg-secondary/30 text-muted-foreground hover:text-foreground"
                                title="Toggle sort direction"
                            >
                                {signalSortDir === 'desc' ? '↓' : '↑'}
                            </button>
                        )}
                    </div>
                </div>
            )}
            </section>

            {/* Repeat Offenders — multi-day anomaly pattern. Lives behind the
                Repeat Offenders tab (Jun 7 2026) so the main P2P ad table sits
                directly under the tab bar instead of being pushed off-screen. */}
            {isOffendersTab && repeatOffenders.length === 0 && (
                <section className="rounded-xl border border-border/70 bg-card p-10 text-center shadow-sm">
                    <AlertTriangle className="mx-auto h-7 w-7 text-muted-foreground" aria-hidden />
                    <p className="mt-3 text-sm font-semibold text-foreground">No repeat offenders</p>
                    <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
                        No merchants priced {selectedAsset} ≥5% off spot across multiple days in the last 30 days. Switch the asset selector to check other tokens.
                    </p>
                </section>
            )}
            {isOffendersTab && repeatOffenders.length > 0 && (
                <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
                    <div className="flex flex-col gap-1 border-b border-border/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-sm font-semibold text-foreground">Repeat anomaly offenders</h2>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                                {selectedAsset} priced ≥5% off spot on ≥2 days in the last 30
                            </p>
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">
                            {repeatOffenders.length} merchant{repeatOffenders.length === 1 ? '' : 's'} flagged
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-muted-foreground border-b border-border">
                                <tr>
                                    <th className="text-left px-2 py-1.5 font-medium">Trader</th>
                                    <th className="text-left px-2 py-1.5 font-medium">Platforms</th>
                                    <th className="text-right px-2 py-1.5 font-medium">Days flagged</th>
                                    <th className="text-right px-2 py-1.5 font-medium">Total ads</th>
                                    <th className="text-right px-2 py-1.5 font-medium">Worst spread</th>
                                    <th className="text-right px-2 py-1.5 font-medium">Last seen</th>
                                    <th className="text-right px-2 py-1.5 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {repeatOffenders.map(o => {
                                    const expanded = offenderExpanded === o.advertiser_name;
                                    const worst = o.worst_premium_pct ?? 0;
                                    const worstColor = Math.abs(worst) >= 10 ? 'text-red-400 font-semibold'
                                        : Math.abs(worst) >= 7 ? 'text-orange-400'
                                        : 'text-amber-400';
                                    return (
                                        <React.Fragment key={`${o.advertiser_name}-${o.advertiser_id || ''}`}>
                                            <tr className="border-b border-border/30 hover:bg-secondary/30">
                                                <td className="px-2 py-1.5 font-medium">{o.advertiser_name}</td>
                                                <td className="px-2 py-1.5">
                                                    <div className="flex gap-1 flex-wrap">
                                                        {o.platforms.map(p => {
                                                            const s = getPlatformStyle(p);
                                                            return (
                                                                <span key={p} className={`px-1.5 py-0.5 rounded text-[10px] ${s.bg} ${s.text}`}>
                                                                    {p}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{o.anomaly_days}</td>
                                                <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{o.total_anomalies}</td>
                                                <td className={`px-2 py-1.5 text-right tabular-nums ${worstColor}`}>
                                                    {worst >= 0 ? '+' : ''}{worst.toFixed(1)}%
                                                </td>
                                                <td className="px-2 py-1.5 text-right text-[10px] text-muted-foreground">
                                                    {o.last_seen ? new Date(o.last_seen).toLocaleDateString() : '—'}
                                                </td>
                                                <td className="px-2 py-1.5 text-right">
                                                    <button
                                                        onClick={() => setOffenderExpanded(expanded ? null : o.advertiser_name)}
                                                        className="text-[10px] text-primary hover:underline"
                                                    >
                                                        {expanded ? 'Hide' : 'Samples'}
                                                    </button>
                                                    {o.profile_link && (
                                                        <a
                                                            href={o.profile_link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="ml-2 text-[10px] text-primary hover:underline"
                                                        >
                                                            Profile ↗
                                                        </a>
                                                    )}
                                                </td>
                                            </tr>
                                            {expanded && o.sample_alerts.length > 0 && (
                                                <tr className="bg-secondary/10">
                                                    <td colSpan={7} className="px-4 py-2">
                                                        <div className="text-[10px] text-muted-foreground mb-1">
                                                            Worst {o.sample_alerts.length} ads:
                                                        </div>
                                                        <div className="grid gap-1">
                                                            {o.sample_alerts.map(s => (
                                                                <div key={s.id} className="flex items-center gap-3 text-[10px]">
                                                                    <span className="font-mono text-muted-foreground w-32 truncate">{s.id}</span>
                                                                    <span className="text-muted-foreground">{s.platform} {s.trade_type}</span>
                                                                    <span className="tabular-nums">₹{s.rate?.toFixed(2)}</span>
                                                                    <span className={`tabular-nums ${s.premium_pct >= 0 ? 'text-red-400' : 'text-amber-400'}`}>
                                                                        {s.premium_pct >= 0 ? '+' : ''}{s.premium_pct.toFixed(1)}%
                                                                    </span>
                                                                    <span className="text-muted-foreground">vs ₹{s.reference_rate?.toFixed(2) ?? '—'}</span>
                                                                    <span className="text-muted-foreground ml-auto">
                                                                        {s.scraped_at ? new Date(s.scraped_at).toLocaleString() : '—'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <p className="border-t border-border/60 px-4 py-2 text-[10px] text-muted-foreground">
                        Each ad's spread is computed against the nearest exchange-ticker reference at scrape time
                        (within ±30 min). Multi-day flags survive normal market drift — these are merchants who
                        priced anomalously across days, not just during a single price swing.
                    </p>
                </section>
            )}

            {/* ═══ Privacy Chain OSINT View ═══ */}
            {isPrivacyTab && (
                <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
                    <div className="flex flex-col gap-1 border-b border-border/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-sm font-semibold text-foreground">Privacy-chain intelligence</h2>
                            <p className="mt-0.5 text-xs text-muted-foreground">Messages mentioning XMR, ZEC, DASH, or swap services</p>
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">{total.toLocaleString()} results</span>
                    </div>
                    <div>
                        {loading ? (
                            <div className="text-center py-8 text-muted-foreground">Loading privacy chain intelligence...</div>
                        ) : records.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <p>No privacy chain mentions found in scraped Telegram data.</p>
                                <p className="text-[11px] mt-1">Run deep backfill: <code className="bg-secondary px-1 rounded">python scripts/telegram_deep_backfill.py</code></p>
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {records.map((r: any, idx: number) => (
                                    <div key={idx} className="px-4 py-3 hover:bg-secondary/30 transition-colors">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                {/* Sender + channel */}
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-medium text-foreground">{r.name || 'Unknown'}</span>
                                                    {r.sender_username && <span className="text-[10px] text-muted-foreground">@{r.sender_username}</span>}
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">#{r.channel_name}</span>
                                                </div>
                                                {/* Chain badges */}
                                                <div className="flex items-center gap-1 mb-1.5">
                                                    {(r.chains_found || []).map((chain: string) => (
                                                        <span key={chain} className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${brandChipNeutral}`}>
                                                            {chain}
                                                        </span>
                                                    ))}
                                                    {(r.mentioned_services || []).map((svc: string) => (
                                                        <span key={svc} className={`text-[9px] px-1.5 py-0.5 rounded ${brandChipNeutral}`}>
                                                            {svc}
                                                        </span>
                                                    ))}
                                                    {(r.mentioned_amounts || []).slice(0, 3).map((amt: string, i: number) => (
                                                        <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded ${brandChipNeutral}`}>
                                                            {amt}
                                                        </span>
                                                    ))}
                                                </div>
                                                {/* Message preview */}
                                                <p className="text-[11px] text-muted-foreground line-clamp-2">{r.message_preview}</p>
                                                {/* Addresses */}
                                                {(r.all_addresses || []).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                                        {(r.all_addresses as string[]).slice(0, 3).map((addr: string, i: number) => (
                                                            <span key={i} className="text-[10px] font-mono bg-muted text-foreground border border-border px-1.5 py-0.5 rounded cursor-pointer hover:bg-muted/80 transition-colors truncate max-w-[200px]"
                                                                  onClick={() => { navigator.clipboard.writeText(addr); }}
                                                                  title={`Click to copy: ${addr}`}>
                                                                {addr.slice(0, 12)}...{addr.slice(-6)}
                                                            </span>
                                                        ))}
                                                        {(r.all_addresses || []).length > 3 && (
                                                            <span className="text-[10px] text-muted-foreground">+{r.all_addresses.length - 3} more</span>
                                                        )}
                                                    </div>
                                                )}
                                                {/* TX hashes */}
                                                {(r.tx_hashes || []).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        <span className="text-[9px] text-amber-700 dark:text-amber-400">TX:</span>
                                                        {(r.tx_hashes as string[]).slice(0, 2).map((h: string, i: number) => (
                                                            <span key={i} className="text-[10px] font-mono bg-muted text-foreground border border-border px-1.5 py-0.5 rounded cursor-pointer hover:bg-muted/80 truncate max-w-[180px]"
                                                                  onClick={() => { navigator.clipboard.writeText(h); }}
                                                                  title={`Click to copy: ${h}`}>
                                                                {h.slice(0, 12)}...{h.slice(-6)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {/* Timestamp */}
                                            <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                                                {r.timestamp ? new Date(r.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* ═══ Cross-Source Identity View (P2P ⇄ Telegram) ═══ */}
            {isCrossSourceTab && <TelegramConnections />}

            {isRingsTab && <RingsTab />}

            {/* Phones by State view */}
            {isUnmaskedStatesTab && <UnmaskedStatesTab />}

            {/* ═══ Table ═══ */}
            {!isPrivacyTab && !isCrossSourceTab && !isOffendersTab && !isRingsTab && !isUnmaskedStatesTab && (
            <section className="min-h-0 overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm" aria-labelledby="p2p-results-title">
                <div className="flex flex-col gap-2 border-b border-border/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 id="p2p-results-title" className="text-sm font-semibold text-foreground">Merchant results</h2>
                        <p className="mt-0.5 text-xs text-muted-foreground" aria-live="polite">
                            {(loading || recordsRefreshing) ? 'Updating results…' : total > 0
                                ? `${((page - 1) * limit) + 1}–${Math.min(page * limit, total)} of ${total.toLocaleString()}`
                                : 'No records'}
                        </p>
                    </div>
                    <nav className="flex items-center gap-1.5" aria-label="P2P results pagination">
                        <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-xs font-medium disabled:opacity-40" aria-label="Previous page">
                            <ChevronLeft className="h-4 w-4" aria-hidden />
                        </button>
                        <span className="min-w-[88px] px-1 text-center text-xs tabular-nums text-muted-foreground">{page} / {totalPages || 1}</span>
                        <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-xs font-medium disabled:opacity-40" aria-label="Next page">
                            <ChevronRight className="h-4 w-4" aria-hidden />
                        </button>
                    </nav>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left text-sm">
                        <thead className="sticky top-0 z-10 border-b border-border bg-muted/95 text-[11px] uppercase tracking-wide text-muted-foreground backdrop-blur">
                            {isCrossPlatformTab ? (
                                <tr>
                                    <th className="px-4 py-3">Trader</th>
                                    <th className="px-4 py-3">Platforms</th>
                                    <th className="px-4 py-3 text-right" title="Available crypto remaining on this ad. Shown in asset units (USDT/BTC/etc.). Binance caps surplus at 2,000,000 USDT, so many top ads sit at that ceiling. INR equivalent is shown beneath when a live price is available.">Available</th>
                                    <th className="px-4 py-3 text-center">Combined Orders</th>
                                    <th className="px-4 py-3">Identifiers</th>
                                    <th className="px-4 py-3">Risk</th>
                                    <th className="px-4 py-3 w-[100px]">Action</th>
                                </tr>
                            ) : (
                                <tr>
                                    <th className="px-4 py-3">Trader</th>
                                    {showPlatformCol && <th className="px-4 py-3">Platform</th>}
                                    {!isTelegramTab && <th className="px-4 py-3 text-right" title="Available crypto remaining on this ad. Shown in asset units (USDT/BTC/etc.). Binance caps surplus at 2,000,000 USDT, so many top ads sit at that ceiling. INR equivalent is shown beneath when a live price is available.">Available</th>}
                                    {!isTelegramTab && <th className="px-4 py-3 text-center">Orders</th>}
                                    {!isTelegramTab && <th className="px-4 py-3 text-right">Rate</th>}
                                    {!isTelegramTab && (
                                        <th className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => setSpreadSort(s => s === 'off' ? 'desc' : s === 'desc' ? 'asc' : 'off')}
                                                className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                                                title="Click to sort by |spread|. Click again to reverse, again to clear.">
                                                Spread
                                                {spreadSort === 'desc' && <span className="text-primary">▼</span>}
                                                {spreadSort === 'asc' && <span className="text-primary">▲</span>}
                                            </button>
                                        </th>
                                    )}
                                    {isExchangeTab && filterSource === 'binance' && <th className="px-4 py-3">Merchant</th>}
                                    {isTelegramTab && <th className="px-4 py-3">Channel</th>}
                                    <th className="px-4 py-3">Identifiers</th>
                                    <th className="px-4 py-3 w-[120px]">Action</th>
                                </tr>
                            )}
                        </thead>
                        <tbody className="divide-y divide-border/60">
                            {loading && filteredRecords.length === 0 ? (
                                <tr><td colSpan={colCount} className="p-10 text-center">
                                    <p className="text-sm font-medium text-muted-foreground">Loading merchants…</p>
                                </td></tr>
                            ) : filteredRecords.length === 0 ? (
                                <tr><td colSpan={colCount} className="p-10 text-center">
                                    <Search className="mx-auto h-7 w-7 text-muted-foreground" aria-hidden />
                                    <p className="mt-3 text-sm font-semibold text-foreground">No matching merchants</p>
                                    <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
                                        Try a broader search or clear the active filters.
                                    </p>
                                    {activeFilterCount > 0 && (
                                        <button type="button" onClick={clearExplorerFilters} className="mt-3 text-xs font-semibold text-primary hover:underline">Clear all filters</button>
                                    )}
                                </td></tr>
                            ) : isCrossPlatformTab ? (
                                /* ─── Cross-Platform Rows ─── */
                                filteredRecords.map((record, idx) => {
                                    const isClickable = !!record.wallet_address || !!record.upi_id;
                                    return (
                                        <tr key={p2pRecordRowKey(record)} className={`hover:bg-muted/30 transition-colors ${isClickable ? 'cursor-pointer' : ''}`}
                                            onClick={() => isClickable && handleRowClick(record)}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <ProviderMonogram name={record.name} />
                                                    <span className="font-medium text-foreground text-sm truncate max-w-[180px]" title={record.name || ''}>{record.name || 'Unknown'}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-1">
                                                    {(record.platforms || []).map(p => {
                                                        const s = getPlatformStyle(p);
                                                        return <span key={p} className={`px-1.5 py-0.5 rounded text-[10px] ${s.bg} ${s.text}`}>{p}</span>;
                                                    })}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right">{formatAssetBalance(record.combined_volume ?? record.volume, record.asset, record.price_rate)}</td>
                                            <td className="px-4 py-3 text-center text-foreground/80 tabular-nums">{(record.combined_orders ?? record.orders)?.toLocaleString() || '\u2014'}</td>
                                            <td className="px-4 py-3">
                                                <IdentifierCell record={record} expandedUpiId={expandedUpiId} toggleUpiExpansion={toggleUpiExpansion} copyToClipboard={copyToClipboard} />
                                            </td>
                                            <td className="px-4 py-3">
                                                <SeverityBadge score={record.risk_score} label={getRiskLabel(record.risk_score)} />
                                                {record.risk_flags && record.risk_flags.length > 0 && (
                                                    <div className="text-[10px] text-red-400 mt-0.5">{record.risk_flags.join(', ')}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {isClickable && (
                                                    <button onClick={(e) => { e.stopPropagation(); handleRowClick(record); }}
                                                        className="flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded text-xs hover:bg-primary/30">
                                                        <Eye className="w-3 h-3" /> Investigate
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                /* ─── Standard P2P / Telegram Rows ─── */
                                filteredRecords.map((record, idx) => {
                                    const isClickable = !!record.wallet_address || !!record.upi_id || !!record.phone;
                                    const regDate = record.merchant_info?.reg_date || null;
                                    const isNew = regDate ? (new Date().getTime() - new Date(regDate).getTime()) / (1000 * 3600 * 24) < 30 : false;
                                    const isTelegramRecord = (record.platform || '') === 'telegram';
                                    const style = getPlatformStyle(record.platform || 'binance');
                                    // Stable per-row key for the PII expansion panel.
                                    // We deliberately combine platform + ad_id (when
                                    // present) + advertiser_id so two ads from the
                                    // same merchant on the same platform expand
                                    // independently — investigators sometimes have
                                    // multiple ads open across the BUY/SELL pair.
                                    const rowKey = p2pRecordRowKey(record);
                                    const isExpanded = expandedRowIds.has(rowKey);
                                    const allPhones = (record.phone_numbers_all && record.phone_numbers_all.length > 0)
                                        ? record.phone_numbers_all
                                        : (record.phone ? [record.phone] : []);

                                    return (
                                        <React.Fragment key={rowKey}>
                                            <tr className={`hover:bg-muted/30 transition-colors ${isClickable ? 'cursor-pointer' : ''}`}
                                                onClick={() => isClickable && handleRowClick(record)}>
                                                {/* Trader — name + date + platform badge (when single-platform tab) */}
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <ProviderMonogram name={record.name} className="bg-slate-700 text-foreground dark:bg-muted dark:text-foreground" />
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-foreground flex items-center gap-1.5 text-sm truncate max-w-[260px]">
                                                                {/* Online dot — green when merchant is active right now,
                                                                    grey when last-seen is known but stale. Helps the
                                                                    investigator know whether an Engage click will get
                                                                    a real-time response. */}
                                                                {(() => {
                                                                    const sig = record.merchant_signals;
                                                                    if (!sig) return null;
                                                                    const lastSeenMs = sig.last_seen ? new Date(sig.last_seen).getTime() : null;
                                                                    const ageMin = lastSeenMs ? (Date.now() - lastSeenMs) / 60000 : null;
                                                                    const isOnline = sig.is_online === true || (ageMin !== null && ageMin < 15);
                                                                    if (!sig.last_seen && sig.is_online !== true && sig.is_online !== false) return null;
                                                                    return (
                                                                        <Circle className={`w-2 h-2 fill-current shrink-0 ${isOnline ? 'text-green-400' : 'text-muted-foreground/50'}`}
                                                                            aria-label={isOnline ? 'Online now' : 'Offline'}
                                                                            data-testid="merchant-online-dot"
                                                                        />
                                                                    );
                                                                })()}
                                                                <span title={record.name || 'Unknown'}>{record.name || 'Unknown'}</span>
                                                                {(record.signal_count || 1) > 1 && (
                                                                    <span className="text-[10px] bg-primary/15 text-foreground px-1 py-0.5 rounded shrink-0">
                                                                        &times;{record.signal_count}
                                                                    </span>
                                                                )}
                                                                {!showPlatformCol && (
                                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${style.bg} ${style.text} shrink-0`}>
                                                                        {record.platform || 'binance'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                                {record.merchant_info?.user_type === 'merchant' && (
                                                                    <span className="text-[10px] bg-yellow-400/10 text-yellow-400 px-1 py-0.5 rounded border border-yellow-400/20">MERCHANT</span>
                                                                )}
                                                                {/* Auto-reply pill — pre-canned merchant message; often
                                                                    the cleanest source of fresh identifiers, so we let
                                                                    the investigator see/copy it without engaging the
                                                                    merchant. Hover to view, click to copy. */}
                                                                {record.merchant_signals?.auto_reply && (
                                                                    <span className="text-[10px] bg-primary/10 text-foreground px-1.5 py-0.5 rounded border border-primary/30 flex items-center gap-0.5 cursor-pointer max-w-[260px] truncate"
                                                                        title={record.merchant_signals.auto_reply}
                                                                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(record.merchant_signals!.auto_reply || ''); }}>
                                                                        <Bot className="w-2.5 h-2.5 shrink-0" /> auto-reply
                                                                    </span>
                                                                )}
                                                                {/* "Targets new accounts" — derived backend-side from
                                                                    the merchant's counterparty filter (e.g. willing to
                                                                    trade with buyers who have 0 prior orders / no KYC).
                                                                    Classic mule-recruitment fingerprint. */}
                                                                {record.merchant_signals?.targets_new_accounts && (
                                                                    <span className="text-[10px] bg-red-500/15 text-red-300 px-1.5 py-0.5 rounded border border-red-500/30"
                                                                        title="Counterparty filter accepts brand-new accounts (zero prior orders / no KYC) — classic mule-recruitment fingerprint">
                                                                        targets new
                                                                    </span>
                                                                )}
                                                                {record.merchant_signals?.pay_time_limit_s && record.merchant_signals.pay_time_limit_s <= 900 && (
                                                                    <span className="text-[10px] bg-orange-500/15 text-orange-300 px-1.5 py-0.5 rounded border border-orange-500/30"
                                                                        title="Short payment window — typical of high-volume mule flow">
                                                                        fast pay {Math.round(record.merchant_signals.pay_time_limit_s / 60)}m
                                                                    </span>
                                                                )}
                                                                {record.merchant_signals?.last_seen && (
                                                                    <span className="text-[10px] text-muted-foreground" title={'Last active: ' + new Date(record.merchant_signals.last_seen).toLocaleString()}>
                                                                        seen {(() => {
                                                                            const d = new Date(record.merchant_signals.last_seen!);
                                                                            const min = Math.floor((Date.now() - d.getTime()) / 60000);
                                                                            if (min < 1) return 'now';
                                                                            if (min < 60) return min + 'm ago';
                                                                            if (min < 1440) return Math.floor(min / 60) + 'h ago';
                                                                            return Math.floor(min / 1440) + 'd ago';
                                                                        })()}
                                                                    </span>
                                                                )}
                                                                {record.scraped_at && (
                                                                    <span className="text-[10px] text-muted-foreground" title={new Date(record.scraped_at).toLocaleString()}>
                                                                        {(() => {
                                                                            const d = new Date(record.scraped_at);
                                                                            const now = new Date();
                                                                            const diffMs = now.getTime() - d.getTime();
                                                                            const diffH = Math.floor(diffMs / 3600000);
                                                                            if (diffH < 1) return 'just now';
                                                                            if (diffH < 24) return `${diffH}h ago`;
                                                                            const diffD = Math.floor(diffH / 24);
                                                                            if (diffD < 7) return `${diffD}d ago`;
                                                                            return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                                                                        })()}
                                                                    </span>
                                                                )}
                                                                {/* Ad lifetime — first_seen_at is preserved across
                                                                    scraper ON CONFLICT UPDATEs, so this is the
                                                                    age of the LISTING (not the merchant). Long-
                                                                    lived ads suggest OTC desks; single-day ads
                                                                    are throwaway mules. Hidden under 1 day (noise). */}
                                                                {record.days_active != null && record.days_active >= 1 && (
                                                                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400"
                                                                        title={record.first_seen_at ? `First seen: ${new Date(record.first_seen_at).toLocaleString()}` : undefined}>
                                                                        {record.days_active >= 30
                                                                            ? `${Math.floor(record.days_active)}d listed`
                                                                            : `${record.days_active}d listed`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <TraderSignalsBadges signals={record.trader_signals} />
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Platform badge — only on All tab */}
                                                {showPlatformCol && (
                                                    <td className="px-4 py-3">
                                                        <span className={`px-2 py-1 rounded text-xs ${style.bg} ${style.text}`}>
                                                            {record.platform || 'binance'}
                                                        </span>
                                                    </td>
                                                )}

                                                {/* Available balance — asset units, NOT INR. See formatAssetBalance. */}
                                                {!isTelegramTab && (
                                                    <td className="px-4 py-3 text-right">
                                                        {isTelegramRecord
                                                            ? <span className="text-muted-foreground/80 text-xs">&mdash;</span>
                                                            : formatAssetBalance(record.volume, record.asset, record.price_rate)}
                                                    </td>
                                                )}

                                                {/* Orders */}
                                                {!isTelegramTab && (
                                                    <td className="px-4 py-3 text-center text-foreground/80 tabular-nums">
                                                        {isTelegramRecord ? '\u2014' : (record.orders?.toLocaleString() || '\u2014')}
                                                    </td>
                                                )}

                                                {/* Rate / Completion */}
                                                {!isTelegramTab && (
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {record.price_rate ? (
                                                            <span className="text-foreground">{formatRate(record.price_rate)}</span>
                                                        ) : record.completion_rate != null ? (
                                                            <span className={`px-2 py-0.5 rounded text-xs ${
                                                                record.completion_rate >= 0.95 ? 'bg-green-500/20 text-green-400' :
                                                                record.completion_rate >= 0.85 ? 'bg-yellow-500/20 text-yellow-400' :
                                                                'bg-red-500/20 text-red-400'
                                                            }`}>
                                                                {(record.completion_rate * 100).toFixed(1)}%
                                                            </span>
                                                        ) : <span className="text-muted-foreground/80">&mdash;</span>}
                                                    </td>
                                                )}

                                                {/* Spread vs spot */}
                                                {!isTelegramTab && (
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {(() => {
                                                            if (!record.price_rate || !referenceRate || referenceRate <= 0) return <span className="text-muted-foreground/80">&mdash;</span>;
                                                            const spread = ((record.price_rate - referenceRate) / referenceRate) * 100;
                                                            if (Math.abs(spread) < 0.1) return <span className="text-muted-foreground text-xs">&mdash;</span>;
                                                            const color = spread > 5 ? 'text-red-400 font-semibold' : spread > 3 ? 'text-orange-400' : spread > 1 ? 'text-amber-400' : spread > 0 ? 'text-muted-foreground' : 'text-green-400';
                                                            return <span className={`text-xs ${color}`}>{spread > 0 ? '+' : ''}{spread.toFixed(1)}%</span>;
                                                        })()}
                                                    </td>
                                                )}

                                                {/* Merchant Profile (Binance tab only) */}
                                                {isExchangeTab && filterSource === 'binance' && (
                                                    <td className="px-4 py-3">
                                                        {record.merchant_info ? (
                                                            <div className="text-xs space-y-0.5">
                                                                <div className={`flex items-center gap-1 ${isNew ? 'text-red-400 font-semibold' : 'text-foreground/80'}`}>
                                                                    <Calendar className="w-3 h-3" />
                                                                    {record.merchant_info.reg_date}
                                                                    {isNew && <span className="text-[10px] bg-red-900/50 px-1 rounded">NEW</span>}
                                                                </div>
                                                                <div className="text-muted-foreground">
                                                                    {record.merchant_info.total_lifetime_orders?.toLocaleString() || '?'} lifetime orders
                                                                </div>
                                                                {record.merchant_info.positive_rate != null && (
                                                                    <div className={`text-[10px] ${
                                                                        record.merchant_info.positive_rate >= 0.95 ? 'text-green-400' :
                                                                        record.merchant_info.positive_rate >= 0.85 ? 'text-yellow-400' :
                                                                        'text-red-400'
                                                                    }`}>
                                                                        {(record.merchant_info.positive_rate * 100).toFixed(1)}% positive
                                                                    </div>
                                                                )}
                                                                {isExpanded && <MerchantTempoSparkline advertiserId={record.id} />}
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <span className="text-muted-foreground/80 text-xs">&mdash;</span>
                                                                {isExpanded && <MerchantTempoSparkline advertiserId={record.id} />}
                                                            </div>
                                                        )}
                                                    </td>
                                                )}

                                                {/* Channel (Telegram tab only) */}
                                                {isTelegramTab && (
                                                    <td className="px-4 py-3">
                                                        {isTelegramRecord ? (
                                                            <span className="text-xs text-blue-400 font-mono">#{(record.source || '').replace('Telegram: ', '')}</span>
                                                        ) : <span className="text-muted-foreground/80 text-xs">&mdash;</span>}
                                                    </td>
                                                )}

                                                {/* Identifiers */}
                                                <td className="px-4 py-3">
                                                    <IdentifierCell record={record} expandedUpiId={expandedUpiId} toggleUpiExpansion={toggleUpiExpansion} copyToClipboard={copyToClipboard} />
                                                </td>

                                                {/* Action — chevron pushed to the far right so the
                                                    visual affordance reads as "this opens the
                                                    whole row's dossier" rather than "another
                                                    button in the middle of the cluster". Engage
                                                    deep-link moved INTO the dossier panel to keep
                                                    this row terse; investigators who want to
                                                    actually chat with the merchant will already
                                                    be opening the dossier to extract identifiers. */}
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1">
                                                        {isClickable && (
                                                            <button onClick={(e) => { e.stopPropagation(); handleRowClick(record); }}
                                                                className="flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded text-xs hover:bg-primary/30 border border-primary/30"
                                                                title="Open this trader's wallet/UPI/phone in the Workbench">
                                                                <Eye className="w-3 h-3" /> Investigate
                                                            </button>
                                                        )}
                                                        {(record.upi_id || record.wallet_address) && (
                                                            <button onClick={(e) => openLinkModal(record, e)}
                                                                className="flex items-center gap-1 px-2 py-1 bg-primary/15 text-foreground rounded text-xs hover:bg-primary/25 border border-primary/30"
                                                                title="Link UPI ID to wallet address">
                                                                <Link2 className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                        {(record.upi_id || record.bank_name || record.phone) && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const params = new URLSearchParams();
                                                                    if (record.upi_id) params.set('vpa', record.upi_id);
                                                                    if (record.bank_name) params.set('account', record.bank_name);
                                                                    if (record.name) params.set('name', record.name);
                                                                    navigate(`/off-ramp?${params.toString()}`);
                                                                }}
                                                                className="flex items-center gap-1 px-2 py-1 bg-amber-500/15 text-amber-700 dark:text-amber-400 rounded text-xs hover:bg-amber-500/25 border border-amber-500/30 font-medium"
                                                                title="Test this merchant in Fiat Off-Ramp Counterparty Match"
                                                            >
                                                                <Zap className="w-3 h-3 fill-amber-500 text-amber-500" /> Off-Ramp
                                                            </button>
                                                        )}
                                                        {record.id && record.platform && record.platform !== 'telegram' && (
                                                            <button
                                                                onClick={(e) => handleRefreshMerchant(record.id!, e)}
                                                                disabled={refreshingMerchant === record.id}
                                                                className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-700 dark:text-green-300 rounded text-xs hover:bg-green-500/30 border border-green-500/30 disabled:opacity-50"
                                                                title="Refresh this merchant's data from exchange">
                                                                <RefreshCw className={`w-3 h-3 ${refreshingMerchant === record.id ? 'animate-spin' : ''}`} />
                                                            </button>
                                                        )}
                                                        {/* PII expand chevron — placed at the end so
                                                            it visually reads as "open the row". */}
                                                        <button onClick={(e) => { e.stopPropagation(); toggleRowExpand(rowKey, allPhones); }}
                                                            className={`ml-1 flex items-center gap-1 px-1.5 py-1 rounded text-xs border transition-colors ${
                                                                isExpanded
                                                                    ? 'bg-muted text-foreground border-border'
                                                                    : 'bg-secondary/40 text-muted-foreground border-border/60 hover:bg-muted/60 hover:text-foreground'
                                                            }`}
                                                            title={isExpanded ? 'Collapse the identifier dossier' : 'Show all harvested identifiers + cached OSINT hits (no live API calls)'}>
                                                            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* ─── PII dossier panel (chevron-expand) ───────────
                                                Renders everything we already harvested for this
                                                merchant — no extra API calls. Cached OSINT only
                                                (from /identity/by-phone). The Enrich button per
                                                phone is opt-in and fires the LIVE LeakOSINT
                                                lookup (~$0.005/call) only on user click. */}
                                            {isExpanded && (
                                                <tr className="bg-secondary/40">
                                                    <td colSpan={colCount} className="px-6 py-4">
                                                        <div className="border-l-2 border-amber-500 pl-4 space-y-3">
                                                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                                                <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-2">
                                                                    <ShieldAlert className="w-3.5 h-3.5" /> Full identifier dossier — every harvest, plus cached OSINT
                                                                </div>
                                                                {/* Engage deep-link — moved here from the
                                                                    main row. Investigators wanting to open
                                                                    the merchant's chat pane on the source
                                                                    exchange (the standard P2P intel
                                                                    extraction technique) are already in
                                                                    the dossier; keeping Engage on the row
                                                                    cluttered every line of the table. */}
                                                                {record.ad_url && (
                                                                    <a href={record.ad_url} target="_blank" rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="flex items-center gap-1 px-3 py-1 bg-amber-500 text-white rounded text-xs font-medium hover:bg-amber-600 border border-amber-600 shadow-sm transition-colors"
                                                                        title="Open the live ad on the source exchange — drops you straight into the merchant chat pane where investigators routinely extract auto-replies, alternate UPI/phone, and other identifiers">
                                                                        <MessageSquare className="w-3 h-3" /> Engage on {record.platform || 'exchange'}
                                                                    </a>
                                                                )}
                                                            </div>

                                                            {/* Phones with OSINT */}
                                                            {allPhones.length > 0 && (
                                                                <div>
                                                                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Phones ({allPhones.length})</div>
                                                                    <div className="space-y-2">
                                                                        {allPhones.map((ph, pi) => {
                                                                            const osint = osintByPhone[ph];
                                                                            const loading = osintLoadingByPhone.has(ph);
                                                                            const ctx = record.merchant_signals?.counterparty_filters as any;
                                                                            // Provenance.phone_contexts is captured by the
                                                                            // scrapers — store the actual line the phone
                                                                            // appeared in (e.g. "WhatsApp 7878347823 RKT").
                                                                            const phoneContext = (record as any).provenance?.phone_contexts?.[ph]
                                                                                || (record as any).merchant_signals?.phone_contexts?.[ph];
                                                                            return (
                                                                                <div key={'ex-ph-' + pi} className="bg-secondary/60 border border-border/60 rounded p-2">
                                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                                        <span className="text-sm font-mono text-primary">📱 {ph}</span>
                                                                                        <Copy className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer"
                                                                                            onClick={(e) => copyToClipboard(ph, e)} />
                                                                                        {ctx && <span className="text-[10px] text-muted-foreground">{/* placeholder for any inline marker */}</span>}
                                                                                        {!osint && !loading && (
                                                                                            <button onClick={(e) => { e.stopPropagation(); triggerPhoneEnrichment(ph, record.wallet_address, record.upi_id); }}
                                                                                                className="ml-auto flex items-center gap-1 px-2.5 py-1 bg-primary text-primary-foreground rounded text-[11px] font-medium hover:bg-brand-gold-hover shadow-sm transition-colors"
                                                                                                title="Run live OSINT (Outris) for this phone — costs one API call. Cached automatically afterwards.">
                                                                                                <ScanLine className="w-2.5 h-2.5" /> Enrich via OSINT
                                                                                            </button>
                                                                                        )}
                                                                                        {loading && (
                                                                                            <span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-1">
                                                                                                <RefreshCw className="w-3 h-3 animate-spin" /> Looking up…
                                                                                            </span>
                                                                                        )}
                                                                                        {osint?.found && (
                                                                                            <span className="ml-auto text-[10px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded border border-green-500/30">
                                                                                                ✓ {osint.hit_count || (osint.sources?.length ?? 0)} hits
                                                                                            </span>
                                                                                        )}
                                                                                        {osint && !osint.found && (
                                                                                            <button onClick={(e) => { e.stopPropagation(); triggerPhoneEnrichment(ph, record.wallet_address, record.upi_id); }}
                                                                                                className="ml-auto flex items-center gap-1 px-2.5 py-1 bg-primary text-primary-foreground rounded text-[11px] font-medium hover:bg-brand-gold-hover shadow-sm transition-colors"
                                                                                                title="No cached OSINT — run live Outris lookup">
                                                                                                <ScanLine className="w-2.5 h-2.5" /> Try live OSINT
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                    {phoneContext && (
                                                                                        <div className="mt-1 text-[10px] text-muted-foreground italic" title="The line of the ad text where this phone appeared">
                                                                                            “{phoneContext.length > 200 ? phoneContext.substring(0, 200) + '…' : phoneContext}”
                                                                                        </div>
                                                                                    )}
                                                                                    {osint?.found && (
                                                                                        <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                                                                                            {osint.names?.length > 0 && (
                                                                                                <div><span className="text-muted-foreground">Names:</span> <span className="text-foreground">{osint.names.join(', ')}</span></div>
                                                                                            )}
                                                                                            {osint.emails?.length > 0 && (
                                                                                                <div><span className="text-muted-foreground">Emails:</span> <span className="text-foreground font-mono">{osint.emails.join(', ')}</span></div>
                                                                                            )}
                                                                                            {osint.phones?.length > 0 && (
                                                                                                <div className="md:col-span-2"><span className="text-muted-foreground">Alt phones:</span> <span className="text-foreground font-mono">{osint.phones.filter((p: string) => p !== ph).join(', ') || '—'}</span></div>
                                                                                            )}
                                                                                            {(osint.address_objects?.length > 0 || osint.addresses?.length > 0) && (
                                                                                                <div className="md:col-span-2">
                                                                                                    <span className="text-muted-foreground">Addresses:</span>
                                                                                                    <ul className="mt-0.5 ml-3 list-disc text-foreground/90">
                                                                                                        {(osint.address_objects?.length > 0 ? osint.address_objects : osint.addresses.map((a: string) => ({ formatted_address: a }))).slice(0, 4).map((a: any, ai: number) => (
                                                                                                            <li key={ai}>
                                                                                                                {a.formatted_address || a.address_text || a.address_text_masked || '—'}
                                                                                                                {a.city && <span className="text-muted-foreground"> · {a.city}{a.state ? ', ' + a.state : ''}{a.pincode ? ' ' + a.pincode : ''}</span>}
                                                                                                                {a.category && <span className="text-[10px] text-amber-300 ml-1">[{a.category}]</span>}
                                                                                                                {typeof a.confidence_score === 'number' && <span className="text-[10px] text-muted-foreground ml-1">{Math.round(a.confidence_score * 100)}%</span>}
                                                                                                            </li>
                                                                                                        ))}
                                                                                                    </ul>
                                                                                                </div>
                                                                                            )}
                                                                                            {(osint.india_sources?.length > 0 || osint.sources?.length > 0) && (
                                                                                                <div className="md:col-span-2 text-[10px] text-muted-foreground">
                                                                                                    Sources: {(osint.india_sources?.length > 0 ? osint.india_sources : osint.sources).join(' · ')}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                    {osint && !osint.found && (
                                                                                        <div className="mt-1 text-[10px] text-muted-foreground">No cached OSINT hits</div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* UPIs */}
                                                            {(record.upi_ids_all?.length || record.upi_id) && (
                                                                <div>
                                                                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">UPIs ({record.upi_ids_all?.length || (record.upi_id ? 1 : 0)})</div>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {(record.upi_ids_all && record.upi_ids_all.length > 0 ? record.upi_ids_all : (record.upi_id ? [record.upi_id] : [])).map((u, ui) => (
                                                                            <div key={'ex-u-' + ui} className="flex items-center gap-1 px-2 py-1 bg-secondary/60 border border-border/60 rounded">
                                                                                <span className="text-xs font-mono text-primary">💳 {u}</span>
                                                                                <Copy className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" onClick={(e) => copyToClipboard(u, e)} />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Wallets */}
                                                            {(record.crypto_addresses_all?.length || record.wallet_address) && (
                                                                <div>
                                                                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Wallets ({record.crypto_addresses_all?.length || (record.wallet_address ? 1 : 0)})</div>
                                                                    <div className="flex flex-col gap-1">
                                                                        {(record.crypto_addresses_all && record.crypto_addresses_all.length > 0 ? record.crypto_addresses_all : (record.wallet_address ? [record.wallet_address] : [])).map((w, wi) => (
                                                                            <div key={'ex-w-' + wi} className="flex items-center gap-1 px-2 py-1 bg-secondary/60 border border-border/60 rounded">
                                                                                <span className="text-xs font-mono text-green-300 break-all">🔗 {w}</span>
                                                                                <Copy className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer shrink-0" onClick={(e) => copyToClipboard(w, e)} />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Telegram handles + bank + auto-reply */}
                                                            {((record.telegram_handles_all && record.telegram_handles_all.length > 0) || record.telegram_username || record.bank_name || record.merchant_signals?.auto_reply || (record.payment_links_all && record.payment_links_all.length > 0)) && (
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                                                    {((record.telegram_handles_all && record.telegram_handles_all.length > 0) || record.telegram_username) && (
                                                                        <div className="bg-secondary/60 border border-border/60 rounded p-2">
                                                                            <div className="text-[10px] uppercase text-muted-foreground mb-1">Telegram</div>
                                                                            <div className="text-sky-300 font-mono">
                                                                                {(record.telegram_handles_all || []).map(h => '@' + h).concat(record.telegram_username ? ['@' + record.telegram_username] : []).join(', ')}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {record.bank_name && (
                                                                        <div className="bg-secondary/60 border border-border/60 rounded p-2">
                                                                            <div className="text-[10px] uppercase text-muted-foreground mb-1">Bank</div>
                                                                            <div className="text-amber-300">🏦 {record.bank_name}</div>
                                                                        </div>
                                                                    )}
                                                                    {record.merchant_signals?.auto_reply && (
                                                                        <div className="bg-secondary/60 border border-border/60 rounded p-2 md:col-span-2">
                                                                            <div className="text-[10px] uppercase text-muted-foreground mb-1 flex items-center gap-1"><Bot className="w-2.5 h-2.5" /> Auto-reply (merchant's pre-canned message)</div>
                                                                            <div className="text-foreground text-xs whitespace-pre-wrap">{record.merchant_signals.auto_reply}</div>
                                                                        </div>
                                                                    )}
                                                                    {record.payment_links_all && record.payment_links_all.length > 0 && (
                                                                        <div className="bg-secondary/60 border border-border/60 rounded p-2 md:col-span-2">
                                                                            <div className="text-[10px] uppercase text-muted-foreground mb-1">Payment shortlinks</div>
                                                                            <div className="text-cyan-300 text-xs break-all">{record.payment_links_all.join(', ')}</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Counterparty filters (mule-recruitment fingerprint) */}
                                                            {record.merchant_signals?.counterparty_filters && Object.keys(record.merchant_signals.counterparty_filters).length > 0 && (
                                                                <div className="bg-secondary/60 border border-border/60 rounded p-2">
                                                                    <div className="text-[10px] uppercase text-muted-foreground mb-1">Counterparty filters (who this merchant will trade with)</div>
                                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] font-mono">
                                                                        {Object.entries(record.merchant_signals.counterparty_filters).map(([k, v]) => (
                                                                            v !== null && v !== undefined && (
                                                                                <div key={k}><span className="text-muted-foreground">{k}:</span> <span className="text-foreground">{String(v)}</span></div>
                                                                            )
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className="text-[10px] text-muted-foreground border-t border-border/40 pt-2 flex items-center gap-2">
                                                                <ScanLine className="w-3 h-3 text-primary" />
                                                                Cached identity lookups are free. Per-phone live OSINT (Outris) costs one paid API call — only fires when you click <span className="text-foreground font-medium">Enrich via OSINT</span>.
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}

                                            {/* Expanded UPI wallets */}
                                            {record.upi_id && expandedUpiId === record.upi_id && (
                                                <tr className="bg-muted/50">
                                                    <td colSpan={colCount} className="px-6 py-4">
                                                        <div className="border-l-2 border-primary pl-4">
                                                            <div className="text-sm font-semibold text-primary mb-2">Linked Wallets for {record.upi_id}</div>
                                                            {walletsLoading ? (
                                                                <div className="text-xs text-muted-foreground">Loading...</div>
                                                            ) : linkedWallets.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    <div className="grid grid-cols-4 gap-4 text-xs text-muted-foreground font-semibold border-b border-border/80 pb-1">
                                                                        <div>Address</div><div>Chain</div><div>Link Type</div><div>Confidence</div>
                                                                    </div>
                                                                    {linkedWallets.map((wallet, widx) => (
                                                                        <div key={widx} className="grid grid-cols-4 gap-4 text-xs hover:bg-muted/30 p-1.5 rounded">
                                                                            <div className="font-mono text-green-400">{wallet.address || '\u2014'}</div>
                                                                            <div className="text-foreground/80">{wallet.chain || '\u2014'}</div>
                                                                            <div className="text-foreground/80">{wallet.link_type || '\u2014'}</div>
                                                                            <div className="text-foreground/80">{wallet.confidence ? `${(wallet.confidence * 100).toFixed(0)}%` : '\u2014'}</div>
                                                                        </div>
                                                                    ))}
                                                                    <button onClick={() => {
                                                                            const addresses = linkedWallets.map(w => w.address).filter(Boolean).join(',');
                                                                            if (addresses) navigate(`/workbench?addresses=${addresses}&chain=TRON`);
                                                                        }}
                                                                        className="mt-2 px-3 py-1.5 bg-primary/20 text-primary rounded text-xs hover:bg-primary/30 flex items-center gap-1">
                                                                        <ExternalLink className="w-3 h-3" /> Investigate All Wallets
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="text-xs text-muted-foreground">No linked wallets found.</div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
            )}

            {/* ═══ UPI-Wallet Link Modal ═══ */}
            {linkModal.open && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setLinkModal({ open: false, upiId: '', walletAddress: '', mode: 'upi-to-wallet' })}>
                    <div className="bg-secondary/30 border border-border/80 rounded-xl p-6 w-[480px] shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                <Link2 className="w-5 h-5 text-primary" />Link UPI to Wallet
                            </h3>
                            <button onClick={() => setLinkModal({ open: false, upiId: '', walletAddress: '', mode: 'upi-to-wallet' })} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">Create a cross-reference link for investigation lookups.</p>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">UPI ID</label>
                                <input type="text" value={linkModal.upiId}
                                    onChange={(e) => setLinkModal(m => ({ ...m, upiId: e.target.value }))}
                                    placeholder="example@paytm"
                                    className="w-full bg-muted border border-border/80 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    readOnly={linkModal.mode === 'upi-to-wallet' && !!linkModal.upiId} />
                            </div>
                            <div className="flex justify-center"><Link2 className="w-4 h-4 text-primary rotate-90" /></div>
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">Wallet Address</label>
                                <input type="text" value={linkModal.walletAddress}
                                    onChange={(e) => setLinkModal(m => ({ ...m, walletAddress: e.target.value }))}
                                    placeholder="T... (TRON) or 0x... (ETH)"
                                    className="w-full bg-muted border border-border/80 rounded-lg px-3 py-2 text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    readOnly={linkModal.mode === 'wallet-to-upi' && !!linkModal.walletAddress} />
                            </div>
                        </div>
                        {linkResult && (
                            <div className={`mt-3 px-3 py-2 rounded text-xs ${linkResult.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                {linkResult.message}
                            </div>
                        )}
                        <button onClick={handleLink} disabled={linkLoading || !linkModal.upiId || !linkModal.walletAddress}
                            className="mt-4 w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-brand-gold-hover disabled:opacity-50">
                            {linkLoading ? 'Linking...' : 'Create Link'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

function P2pStatCard({ label, value, tone = 'neutral', icon, suffix }: {
    label: string;
    value?: number;
    tone?: IntelStatTone;
    icon: LucideIcon;
    suffix?: string;
}) {
    return (
        <div className="rounded-xl border border-border/70 bg-card p-3 shadow-sm">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="mt-1 text-xl font-bold tabular-nums text-foreground">
                        {value != null ? value.toLocaleString() : '—'}
                    </div>
                    {suffix && <div className="mt-0.5 text-[11px] text-muted-foreground">{suffix}</div>}
                </div>
                <IntelStatIcon icon={icon} tone={tone} />
            </div>
        </div>
    );
}

export default P2PIntelligence;
